// ============================================================================
// SPECIALIST PACK BUILDER VIEW
//
// 9-section guided builder for specialists to author clinical decision Packs.
// Sections: metadata, scope, redirectFlags, redFlags, fields,
//           attachmentRequirements, notYetSteps, patientMessages, review
//
// Lifecycle: draft → active → archived
// Path: tenants/{tenantId}/specialties/{specialtyId}/packs/{packDocId}
// ============================================================================

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { safeUserError } from '../core/safeError.js';
import {
  Package, Plus, Trash2, GripVertical, ChevronRight, ChevronLeft,
  ChevronDown, ChevronUp,
  Save, Eye, Clock, AlertTriangle, CheckCircle, ArrowRight, FileText,
  Flag, Shield, Clipboard, Paperclip, MessageSquare, Search,
  Edit3, Archive, X, CheckSquare, Square, Lock, HelpCircle, Sparkles,
} from 'lucide-react';
import {
  createPackDraft,
  updatePackDraft,
  publishPack,
  publishNewPackVersion,
  selectNewVersionId,
  archivePack,
  getPacksForSpecialist,
  getPackDraft,
  deletePackDraft,
  PACK_STATUS,
} from '../core/store.packs.js';
import { ALLOWED_FIELD_TYPES } from '../core/schemas.js';
import {
  EVIDENCE_DOCUMENT_TYPES,
  EVIDENCE_DOCUMENT_TYPE_LABELS,
  EVIDENCE_VALUE_TYPES,
  EVIDENCE_FIELD_MODES,
  RED_FLAG_EVIDENCE_MODE,
  normalizePackEvidenceMaps,
  validatePackEvidenceMaps,
} from '../core/evidenceMap.js';
import {
  draftFieldEvidenceMap,
  draftOptionEvidence,
  draftRedFlagEvidenceMap,
  draftAttachmentEvidenceMap,
  evidenceMapHasValues,
} from '../core/evidenceMapDrafting.js';
import {
  autoLinkPackEvidence,
  evidenceMapStatus,
  EVIDENCE_STATUS,
  EVIDENCE_STATUS_LABELS,
} from '../core/evidenceAutoLink.js';
import { resolveConceptModule } from '../core/specialtyCatalog.js';
import { selfHealClaimsCF } from '../core/functions.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { getAuth } from 'firebase/auth';
import PACK_BUILDER_HELP from '../core/packBuilderHelp.js';
import PackVersionHistoryDrawer from './PackVersionHistoryDrawer.jsx';

// ============================================================================
// CONSTANTS
// ============================================================================

const SECTIONS = [
  { id: 'metadata', label: 'Pack Setup', icon: FileText },
  { id: 'scope', label: 'Referral Context & Escalation', icon: Search },
  { id: 'redirectFlags', label: 'Redirect Flags', icon: ArrowRight },
  { id: 'redFlags', label: 'Red Flags', icon: Flag },
  { id: 'fields', label: 'Clinical Fields', icon: Clipboard },
  { id: 'attachments', label: 'Attachments', icon: Paperclip },
  { id: 'notYetSteps', label: 'Not-Yet Recovery', icon: Shield },
  { id: 'patientMessages', label: 'Outcome Messages', icon: MessageSquare },
  { id: 'review', label: 'Review & Publish', icon: Eye },
];

const FIELD_TYPE_OPTIONS = ALLOWED_FIELD_TYPES.map((t) => ({ value: t, label: t }));

const ATTACHMENT_TYPE = {
  CLINICAL_PHOTO:  'CLINICAL_PHOTO',
  LABS:            'LABS',
  IMAGING_REPORT:  'IMAGING_REPORT',
  REFERRAL_NOTE:   'REFERRAL_NOTE',
  INSURANCE:       'INSURANCE',
  MED_LIST:        'MED_LIST',
  OTHER_DOC:       'OTHER_DOC',
};

const ATTACHMENT_TYPE_LABELS = {
  CLINICAL_PHOTO:  'Clinical Photo',
  LABS:            'Lab Results',
  IMAGING_REPORT:  'Imaging Report',
  REFERRAL_NOTE:   'Referral Note',
  INSURANCE:       'Insurance Info',
  MED_LIST:        'Medication List',
  OTHER_DOC:       'Other Document',
};

const EMPTY_FIELD = {
  id: '',
  label: '',
  type: 'select',
  required: true,
  affectsOutcome: true,
  notYetInstruction: '',
  notYetStepIds: [],
  options: [],
  helpText: '',
  category: 'clinical',
};

const EMPTY_RED_FLAG = {
  id: '',
  label: '',
  description: '',
  severity: 'high',
  forceUrgent: true,
};

const EMPTY_REDIRECT = {
  id: '',
  label: '',
  description: '',
  redirectReason: '',
  target: { type: 'pack', targetId: '', label: '' },
};

// ============================================================================
// PHASE 2 — PUBLISH INTEGRITY GATE
// ----------------------------------------------------------------------------
// Hard publish-blocking checks for the guided builder, expressed against the
// builder's flat draft shape. These mirror the rules validatePack() (see
// src/core/schema.js) enforces on the JSON-import path in PackManagerView, so
// BOTH authoring surfaces enforce the same minimum quality bar:
//   - Pack name present
//   - Semantic version (stamped onto every referral's audit record)
//   - At least one clinical field
//   - ROUTINE / NOT_YET / URGENT outcome messages non-blank
//   - REDIRECT outcome message non-blank WHERE APPLICABLE (redirect flags exist)
// Red-flag presence is NOT enforced here — validatePack allows zero red flags —
// it is handled separately as a soft, confirmable gate in handlePublish (Task 3).
// Pure function (no PHI, no Firestore, no Gate logic); returns operational,
// GP-facing issue strings (empty array = passes).
// ============================================================================

const SEMVER_PATTERN = /^\d+\.\d+\.\d+$/;

const REQUIRED_OUTCOME_MESSAGES = [
  { key: 'routine', label: 'ROUTINE' },
  { key: 'not_yet', label: 'NOT YET' },
  { key: 'urgent',  label: 'URGENT' },
];

function buildPublishIntegrityIssues(draft) {
  const issues = [];
  const d = draft || {};

  if (!d.name || !d.name.trim()) {
    issues.push('Add a Pack name (Pack Setup section). Without a name the Gate and routing cannot identify this pathway.');
  }

  const versionId = (d.versionId || '').trim();
  if (!SEMVER_PATTERN.test(versionId)) {
    issues.push(`Use a semantic version like 1.0.0 (Pack Setup section)${versionId ? ` — "${versionId}" is not valid` : ''}. The version is stamped onto every referral's audit record.`);
  }

  if (!Array.isArray(d.fields) || d.fields.length === 0) {
    issues.push('Add at least one clinical field (Clinical Fields section). A Pack with no fields cannot judge referral completeness.');
  }

  const msgs = d.patientMessageTemplates || {};
  REQUIRED_OUTCOME_MESSAGES.forEach(({ key, label }) => {
    if (!msgs[key] || !msgs[key].trim()) {
      issues.push(`Add the ${label} outcome message (Outcome Messages section). This protects the Gate outcome shown to the GP — blank means the GP sees no guidance when the Gate returns ${label}.`);
    }
  });

  const redirectCount =
    (Array.isArray(d.intraSpecialtyRedirectFlags) ? d.intraSpecialtyRedirectFlags.length : 0) +
    (Array.isArray(d.crossSpecialtyRedirectFlags) ? d.crossSpecialtyRedirectFlags.length : 0);
  if (redirectCount > 0 && (!msgs.redirect || !msgs.redirect.trim())) {
    issues.push('Add the REDIRECT outcome message (Outcome Messages section). This Pack defines redirect flags, so the GP needs the message explaining where to send the patient instead.');
  }

  return issues;
}

// Publish-blocked panel — lists every blocking issue in operational language
// (never a single vague toast) and, when the only remaining gate is the
// zero-red-flags safety rule, offers an explicit "No red flags by design"
// confirmation. Presentational only.
function PublishBlockedPanel({ block, saving, onConfirmNoRedFlags, onDismiss }) {
  const { issues = [], needsRedFlagConfirm = false } = block || {};
  const onlyRedFlagGate = needsRedFlagConfirm && issues.length === 0;
  return (
    <div style={{ background: 'var(--rg-status-not-yet-bg)', border: '1px solid var(--rg-status-not-yet-border)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h4 style={{ fontSize: 14, fontWeight: 700, color: 'var(--rg-status-not-yet-text)', display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
          <AlertTriangle size={15} /> This Pack cannot publish yet
        </h4>
        <button onClick={onDismiss} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--rg-status-not-yet-text)' }} title="Dismiss">
          <X size={15} />
        </button>
      </div>
      {issues.length > 0 && (
        <ul style={{ margin: '0 0 4px 0', paddingLeft: 20 }}>
          {issues.map((issue, i) => (
            <li key={i} style={{ fontSize: 13, color: 'var(--rg-status-not-yet-text)', marginBottom: 6, lineHeight: 1.45 }}>{issue}</li>
          ))}
        </ul>
      )}
      {needsRedFlagConfirm && (
        <div style={{ marginTop: issues.length > 0 ? 12 : 0, paddingTop: issues.length > 0 ? 12 : 0, borderTop: issues.length > 0 ? '1px solid var(--rg-status-not-yet-border)' : 'none' }}>
          <p style={{ fontSize: 13, color: 'var(--rg-status-not-yet-text)', marginBottom: 8, lineHeight: 1.45 }}>
            This Pack has <strong>no red flags</strong>, so the Gate can never return URGENT for it. Add at least one red flag (Red Flags section), or confirm this is intentional. This protects the Gate outcome shown to the GP.
          </p>
          <button
            onClick={onConfirmNoRedFlags}
            disabled={saving || !onlyRedFlagGate}
            title={onlyRedFlagGate ? undefined : 'Resolve the issues above first'}
            style={(saving || !onlyRedFlagGate) ? btnDisabled : btnPrimary}
          >
            <CheckCircle size={14} /> No red flags by design — publish anyway
          </button>
        </div>
      )}
    </div>
  );
}


// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function SpecialistPackBuilderView({ activeFocusTarget, onLearningContext }) {
  const [mode, setMode] = useState('list'); // 'list' | 'edit'
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activePack, setActivePack] = useState(null);
  const [activeSection, setActiveSection] = useState(0);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Phase 2 — publish integrity gate.
  // publishBlock: { issues: string[], needsRedFlagConfirm: boolean } | null
  // noRedFlagsConfirmed: per-session "No red flags by design" confirmation for
  // the currently-open pack (reset whenever a different pack is opened/created).
  const [publishBlock, setPublishBlock] = useState(null);
  const [noRedFlagsConfirmed, setNoRedFlagsConfirmed] = useState(false);
  // Active-pack "Publish new version" confirmation + post-publish success notice.
  const [showVersionConfirm, setShowVersionConfirm] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);
  // Read-only Version History drawer target: { specialtyId, packId, name } | null
  const [historyPack, setHistoryPack] = useState(null);

  const errorRef = useRef(null);

  // Draft state mirrors Firestore pack doc
  const [draft, setDraft] = useState(null);

  const { user } = useAuth();
  const tenantId = user?.tenantId;
  const specialtyIds = user?.specialtyIds || [];
  const uid = user?.uid;

  useEffect(() => {
    if (onLearningContext) {
      onLearningContext(mode === 'list' ? { packBuilderList: true } : { packEditorOpen: true });
    }
  }, [onLearningContext, mode]);

  useEffect(() => {
    if (!activeFocusTarget) return;
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-learning-target="${activeFocusTarget}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
    return () => clearTimeout(timer);
  }, [activeFocusTarget, mode]);

  const hl = (id) => {
    if (activeFocusTarget !== id) return '';
    if (id === 'pack-builder-list' && mode !== 'list') return '';
    if (id === 'pack-editor-guidance-icon' && mode !== 'edit') return '';
    return 'ring-2 ring-emerald-400/20';
  };

  // ========================================================================
  // LOAD PACK LIST
  // ========================================================================

  const [claimsHealed, setClaimsHealed] = useState(false);

  const ensureClaims = useCallback(async () => {
    if (claimsHealed) return;
    try {
      const result = await selfHealClaimsCF();
      if (result.healed) {
        const currentUser = getAuth().currentUser;
        if (currentUser) await currentUser.getIdToken(true);
      }
      setClaimsHealed(true);
    } catch (err) {
      console.error('[PackBuilder] selfHealClaims failed:', err.message);
      setClaimsHealed(true);
    }
  }, [claimsHealed]);

  const loadPacks = useCallback(async () => {
    if (!tenantId) {
      setLoading(false);
      setError('No tenant assigned. Contact your admin.');
      return;
    }
    if (specialtyIds.length === 0) {
      setLoading(false);
      setError('You are not assigned to a specialty. Ask your admin to assign your specialty before creating Packs.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await ensureClaims();
      const results = await getPacksForSpecialist(tenantId, specialtyIds);
      setPacks(results);
    } catch (err) {
      console.error('[PackBuilder] loadPacks failed:', err.message, err);
      setError(safeUserError(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, JSON.stringify(specialtyIds), ensureClaims]);

  useEffect(() => { loadPacks(); }, [loadPacks]);

  // ========================================================================
  // CREATE NEW DRAFT
  // ========================================================================

  const handleCreateDraft = async () => {
    if (!tenantId) {
      setError('No tenant assigned. Contact your admin.');
      return;
    }
    if (specialtyIds.length === 0) {
      setError('You are not assigned to a specialty. Ask your admin to assign your specialty before creating Packs.');
      return;
    }
    setError(null);
    try {
      await ensureClaims();
      const { packDocId } = await createPackDraft(tenantId, specialtyIds[0], {
        name: '',
        description: '',
        createdByUid: uid,
      });
      const packData = await getPackDraft(tenantId, specialtyIds[0], packDocId);
      setDraft(packData);
      setActivePack(packData);
      setActiveSection(0);
      setMode('edit');
      setDirty(false);
      setPublishBlock(null);
      setNoRedFlagsConfirmed(false);
      setShowVersionConfirm(false);
      setSuccessMsg(null);
    } catch (err) {
      console.error('[PackBuilder] createDraft failed:', err.message, err);
      setError(safeUserError(err));
    }
  };

  // ========================================================================
  // OPEN EXISTING PACK FOR EDITING
  // ========================================================================

  const normalizePatientMessages = (msgs) => {
    if (!msgs) return { urgent: '', routine: '', not_yet: '' };
    return {
      urgent: msgs.urgent || msgs.Urgent || '',
      routine: msgs.routine || msgs.Routine || '',
      not_yet: msgs.not_yet || msgs.NotYet || msgs.notYet || '',
    };
  };

  const flattenPackForBuilder = (data) => {
    const packMap = data.pack;
    if (!packMap || typeof packMap !== 'object') return data;
    return {
      ...data,
      fields: data.fields?.length ? data.fields : (packMap.fields || []),
      redFlags: data.redFlags?.length ? data.redFlags : (packMap.redFlags || []),
      intraSpecialtyRedirectFlags: data.intraSpecialtyRedirectFlags?.length
        ? data.intraSpecialtyRedirectFlags
        : (packMap.intraSpecialtyRedirectFlags || []),
      crossSpecialtyRedirectFlags: data.crossSpecialtyRedirectFlags?.length
        ? data.crossSpecialtyRedirectFlags
        : (packMap.crossSpecialtyRedirectFlags || []),
      notYetNextSteps: data.notYetNextSteps?.length
        ? data.notYetNextSteps
        : (packMap.notYetSteps || packMap.notYetNextSteps || []),
      patientMessageTemplates: data.patientMessageTemplates?.urgent
        ? data.patientMessageTemplates
        : normalizePatientMessages(packMap.patientMessages || packMap.patientMessageTemplates),
      escalationOptions: data.escalationOptions?.length
        ? data.escalationOptions
        : (packMap.escalationOptions || []),
      attachmentRequirements: data.attachmentRequirements?.length
        ? data.attachmentRequirements
        : (packMap.attachmentRequirements || []),
      telehealthEnabled: data.telehealthEnabled ?? packMap.telehealthEnabled ?? true,
      name: data.name || packMap.metadata?.name || packMap.name || 'Untitled Pack',
      description: data.description || packMap.metadata?.description || packMap.description || '',
      versionId: data.versionId || packMap.metadata?.version || '1.0.0',
    };
  };

  const handleOpenPack = async (pack) => {
    setError(null);
    try {
      const data = await getPackDraft(tenantId, pack.specialtyId, pack.id);
      if (!data) throw new Error('Pack not found');
      const normalized = flattenPackForBuilder(data);
      setDraft(normalized);
      setActivePack(normalized);
      setActiveSection(0);
      setMode('edit');
      setDirty(false);
      setPublishBlock(null);
      setNoRedFlagsConfirmed(false);
      setShowVersionConfirm(false);
      setSuccessMsg(null);
    } catch (err) {
      setError(safeUserError(err));
    }
  };

  // ========================================================================
  // SAVE CURRENT SECTION
  // ========================================================================

  const handleSave = async () => {
    if (!draft || !activePack) return;
    // Active packs no longer save content in place. Content changes to an active
    // Pack must go through "Publish new version" (new immutable version + archive
    // of the prior one). Only DRAFT content persists via Save.
    if (draft.status !== PACK_STATUS.DRAFT) return;
    setSaving(true);
    setError(null);
    try {
      // Phase 2C-2d: silently prepare evidence maps in the background. autoLink
      // fills only EMPTY maps from the concept library; manual / reviewed /
      // existing / legacy maps are preserved. Then normalize (Phase 2C-1) —
      // empty maps are omitted; packs without evidence maps are unchanged.
      const specialtyId = draft.specialtyId || activePack.specialtyId;
      // Catalog V2 inheritance: optional tracks / service packs resolve to their
      // parent concept module for evidence provenance only. pack.specialtyId,
      // storage, and recognition are unchanged.
      const evidenceSpecialtyId = resolveConceptModule(specialtyId) || specialtyId;
      const normalized = normalizePackEvidenceMaps(autoLinkPackEvidence(draft, evidenceSpecialtyId));
      // Reflect the background preparation in-memory (only fills empty maps, so
      // no specialist content is clobbered) so subtle status chips update.
      setDraft(normalized);
      const { id, createdAt, updatedAt, ...updates } = normalized;
      await updatePackDraft(tenantId, activePack.specialtyId, activePack.id, updates);
      setDirty(false);
    } catch (err) {
      setError(safeUserError(err));
    } finally {
      setSaving(false);
    }
  };

  // ========================================================================
  // PUBLISH
  // ========================================================================

  // Shared publish preparation + hard-issue collection. Used by BOTH the draft
  // publish path (handlePublish) and the active "Publish new version" path
  // (handlePublishNewVersion) so they enforce the IDENTICAL integrity gate.
  // In-memory only (autoLink/validate); no Firestore writes.
  const collectPublishPlan = () => {
    const draftFields = draft?.fields || [];
    const draftSteps = draft?.notYetNextSteps || [];
    const draftStepIds = new Set(draftSteps.map((s) => s.id));
    const draftFieldIds = new Set(draftFields.map((f) => f.id));

    // Phase 2C-2d: silently prepare evidence maps before validation + publish.
    // autoLink fills only EMPTY maps; manual / reviewed / existing maps preserved.
    // Catalog V2 inheritance: resolve optional tracks / service packs to their
    // parent concept module for evidence provenance only (pack.specialtyId unchanged).
    const rawSpecialtyId = draft?.specialtyId || activePack.specialtyId;
    const evidenceSpecialtyId = resolveConceptModule(rawSpecialtyId) || rawSpecialtyId;
    const prepared = autoLinkPackEvidence(draft || {}, evidenceSpecialtyId);

    const fieldErrors = [];
    draftFields.forEach((field) => {
      if (field.required && !field.affectsOutcome) {
        fieldErrors.push(`"${field.label || field.id}" is required but affectsOutcome is not set.`);
      }
      if (field.required && field.affectsOutcome && !field.notYetInstruction) {
        fieldErrors.push(`"${field.label || field.id}" is required but has no Not-Yet recovery instruction.`);
      }
      if (field.notYetStepIds?.length > 0) {
        field.notYetStepIds.forEach((sid) => {
          if (!draftStepIds.has(sid)) {
            fieldErrors.push(`"${field.label || field.id}" references missing Not-Yet step "${sid}".`);
          }
        });
      }
      if (field.type === 'slider') {
        const fLabel = field.label || field.id;
        if (field.min == null) fieldErrors.push(`Slider "${fLabel}" is missing a min value.`);
        if (field.max == null) fieldErrors.push(`Slider "${fLabel}" is missing a max value.`);
        if (field.step == null) fieldErrors.push(`Slider "${fLabel}" is missing a step value.`);
        if (field.min != null && field.max != null && field.max <= field.min) {
          fieldErrors.push(`Slider "${fLabel}" max must be greater than min.`);
        }
        if (field.step != null && field.step <= 0) {
          fieldErrors.push(`Slider "${fLabel}" step must be greater than 0.`);
        }
        if (!field.anchors || Object.keys(field.anchors).length === 0) {
          fieldErrors.push(`Slider "${fLabel}" must have at least one anchor label before activation.`);
        }
      }
    });
    draftSteps.forEach((step) => {
      if (step.requiredArtifacts?.length > 0) {
        step.requiredArtifacts.forEach((art) => {
          if (art.type === 'field' && !draftFieldIds.has(art.refId)) {
            fieldErrors.push(`Not-Yet step "${step.label || step.id}" references missing field "${art.refId}".`);
          }
        });
      }
    });
    const draftAttachments = (draft?.attachmentRequirements || []).map(normalizeAttachment);
    draftAttachments.forEach((att) => {
      if (!att.label) {
        fieldErrors.push(`Attachment "${att.id}" is missing a label.`);
      }
      if (!att.attachmentType) {
        fieldErrors.push(`Attachment "${att.label || att.id}" is missing an attachment type.`);
      }
    });

    // Evidence Assist maps — validate the auto-prepared shapes so invalid enums /
    // orphaned option keys / bad document-type hints block publish.
    fieldErrors.push(...validatePackEvidenceMaps(prepared));

    // Intra-specialty redirect resolution: every configured redirect must link
    // to an existing sibling pack.
    const intraFlags = draft?.intraSpecialtyRedirectFlags || [];
    const configuredFlags = intraFlags.filter((flag) => flag.label?.trim() || flag.target?.targetId);
    const otherPackIds = new Set(packs.filter((p) => p.id !== draft?.id).map((p) => p.id));
    const unresolvedRedirects = configuredFlags.filter((flag) => {
      if (!flag.target?.targetId) return true;
      if (!otherPackIds.has(flag.target.targetId)) return true;
      return false;
    });
    if (unresolvedRedirects.length > 0) {
      fieldErrors.push('Every intra-specialty redirect must link to an existing pack in this specialty (Redirect Flags section). Pick a target pack for each redirect.');
    }

    // Phase 2: Publish Integrity Gate — mirror validatePack's rules (name, semver
    // version, ≥1 field, ROUTINE/NOT_YET/URGENT outcome messages, REDIRECT message
    // where applicable).
    fieldErrors.push(...buildPublishIntegrityIssues(draft));

    const emptyFlags = intraFlags.length - configuredFlags.length;
    return { prepared, fieldErrors, configuredFlags, emptyFlags };
  };

  // Shared red-flag soft gate + block UI. Returns true if blocked (caller returns).
  const blockIfPublishIssues = (fieldErrors, opts = {}) => {
    const zeroRedFlags = (draft?.redFlags || []).length === 0;
    const redFlagConfirmed = noRedFlagsConfirmed || opts.confirmNoRedFlags === true;
    const needsRedFlagConfirm = zeroRedFlags && !redFlagConfirmed;
    if (fieldErrors.length > 0 || needsRedFlagConfirm) {
      console.error('[PackBuilder] Publish blocked:', { issues: fieldErrors, needsRedFlagConfirm });
      setError(null);
      setShowVersionConfirm(false);
      setPublishBlock({ issues: fieldErrors, needsRedFlagConfirm });
      setTimeout(() => errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
      return true;
    }
    return false;
  };

  // ── Draft publish (draft → active) ──────────────────────────────────────────
  const handlePublish = async (opts = {}) => {
    if (!activePack) return;
    setPublishBlock(null);
    const { prepared, fieldErrors, configuredFlags, emptyFlags } = collectPublishPlan();
    if (blockIfPublishIssues(fieldErrors, opts)) return;

    if (emptyFlags > 0) {
      setDraft((prev) => ({ ...prev, intraSpecialtyRedirectFlags: configuredFlags }));
    }

    setSaving(true);
    setError(null);
    try {
      const baseDraft = emptyFlags > 0 ? { ...prepared, intraSpecialtyRedirectFlags: configuredFlags } : prepared;
      const cleanDraft = normalizePackEvidenceMaps(baseDraft);
      const { id, createdAt, updatedAt, ...updates } = cleanDraft;
      await updatePackDraft(tenantId, activePack.specialtyId, activePack.id, updates);
      await publishPack(tenantId, activePack.specialtyId, activePack.id);
      setMode('list');
      setDraft(null);
      setActivePack(null);
      setPublishBlock(null);
      setNoRedFlagsConfirmed(false);
      setSuccessMsg('Pack published.');
      await loadPacks();
    } catch (err) {
      console.error('[PackBuilder] Publish failed:', err.message, err);
      setError(safeUserError(err));
      setTimeout(() => errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    } finally {
      setSaving(false);
    }
  };

  // ── Active "Publish new version" — step 1: validate, then show confirm ───────
  const handlePublishNewVersion = (opts = {}) => {
    if (!activePack || !draft) return;
    setPublishBlock(null);
    const { fieldErrors } = collectPublishPlan();
    if (blockIfPublishIssues(fieldErrors, opts)) return;
    setShowVersionConfirm(true);
  };

  // Step 2: write — create the new immutable version doc + archive the prior one.
  const doPublishNewVersion = async () => {
    if (!activePack || !draft) return;
    setShowVersionConfirm(false);
    const { prepared, configuredFlags, emptyFlags } = collectPublishPlan();
    setSaving(true);
    setError(null);
    try {
      const baseDraft = emptyFlags > 0 ? { ...prepared, intraSpecialtyRedirectFlags: configuredFlags } : prepared;
      const cleanDraft = normalizePackEvidenceMaps(baseDraft);
      const { newVersionId } = await publishNewPackVersion(
        tenantId,
        activePack.specialtyId,
        activePack.id,
        cleanDraft,
        { createdByUid: uid, requestedVersionId: (draft.versionId || '').trim() },
      );
      setMode('list');
      setDraft(null);
      setActivePack(null);
      setPublishBlock(null);
      setNoRedFlagsConfirmed(false);
      setSuccessMsg(`Published v${newVersionId} as the active Pack. The previous version was archived.`);
      await loadPacks();
    } catch (err) {
      console.error('[PackBuilder] Publish new version failed:', err.message, err);
      setError(safeUserError(err));
      setTimeout(() => errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    } finally {
      setSaving(false);
    }
  };

  // ========================================================================
  // ARCHIVE
  // ========================================================================

  const handleArchive = async (pack) => {
    setError(null);
    try {
      await archivePack(tenantId, pack.specialtyId, pack.id);
      await loadPacks();
    } catch (err) {
      setError(safeUserError(err));
    }
  };

  // ========================================================================
  // DELETE DRAFT
  // ========================================================================

  const handleDelete = async (pack) => {
    setError(null);
    try {
      await deletePackDraft(tenantId, pack.specialtyId, pack.id);
      await loadPacks();
    } catch (err) {
      setError(safeUserError(err));
    }
  };

  // ========================================================================
  // DRAFT FIELD UPDATER
  // ========================================================================

  const updateDraft = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  // ========================================================================
  // RENDER: LIST MODE
  // ========================================================================

  // Active "Publish new version" — the current active version plus the version
  // that would be created (auto patch-bump, or a higher manual semver typed in
  // the Version field). Used by the footer button + the confirmation panel.
  const currentActiveVersion = activePack?.versionId || draft?.versionId || '1.0.0';
  let newVersionPreview = '—';
  try {
    newVersionPreview = selectNewVersionId(currentActiveVersion, (draft?.versionId || '').trim());
  } catch {
    newVersionPreview = '—';
  }

  if (mode === 'list') {
    return (
      <div style={{ padding: 32, maxWidth: 900, margin: '0 auto' }} className={`rounded-xl transition-all ${hl('pack-builder-list')}`} data-learning-target="pack-builder-list">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--rg-text-primary)' }}>
            <Package size={24} /> Pack Builder
          </h1>
          <button
            onClick={handleCreateDraft}
            style={btnPrimary}
          >
            <Plus size={16} /> New Pack
          </button>
        </div>

        {successMsg && (
          <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(52,211,153,0.4)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <span style={{ fontSize: 13, color: '#6ee7b7', display: 'flex', alignItems: 'center', gap: 6 }}>
              <CheckCircle size={14} /> {successMsg}
            </span>
            <button onClick={() => setSuccessMsg(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#6ee7b7' }} title="Dismiss">
              <X size={14} />
            </button>
          </div>
        )}

        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

        {loading && <p style={{ color: 'var(--rg-text-secondary)' }}>Loading packs...</p>}

        {!loading && packs.length === 0 && (
          <div style={{ textAlign: 'center', padding: 48, color: 'var(--rg-text-secondary)' }}>
            <Package size={48} style={{ marginBottom: 16, opacity: 0.4 }} />
            <p>No packs yet. Create your first pack to define referral criteria.</p>
          </div>
        )}

        {!loading && packs.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {packs.map((pack) => (
              <PackListItem
                key={pack.id}
                pack={pack}
                onOpen={handleOpenPack}
                onArchive={handleArchive}
                onDelete={handleDelete}
                onHistory={setHistoryPack}
              />
            ))}
          </div>
        )}
        {historyPack && (
          <PackVersionHistoryDrawer
            tenantId={tenantId}
            specialtyId={historyPack.specialtyId}
            packId={historyPack.packId}
            packName={historyPack.name}
            onClose={() => setHistoryPack(null)}
          />
        )}
      </div>
    );
  }

  // ========================================================================
  // RENDER: EDIT MODE (Builder Wizard)
  // ========================================================================

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }} data-learning-target="pack-editor-shell">
      {/* Sidebar navigation */}
      <div style={{ width: 240, borderRight: '1px solid var(--rg-border-subtle)', padding: '16px 0', overflowY: 'auto', background: 'var(--rg-bg-surface)' }}>
        <div style={{ padding: '0 16px 16px', borderBottom: '1px solid var(--rg-border-subtle)', marginBottom: 8 }}>
          <button onClick={() => { setMode('list'); setDraft(null); setActivePack(null); }} style={btnGhost}>
            <ChevronLeft size={14} /> Back to List
          </button>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginTop: 8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--rg-text-primary)' }}>
            {draft?.name || 'Untitled Pack'}
          </h3>
          <span style={{ fontSize: 11, color: statusColor(draft?.status), fontWeight: 600, textTransform: 'uppercase' }}>
            {draft?.status || 'draft'}
          </span>
        </div>
        {SECTIONS.map((sec, idx) => {
          const Icon = sec.icon;
          const isActive = idx === activeSection;
          return (
            <button
              key={sec.id}
              onClick={() => setActiveSection(idx)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '10px 16px', border: 'none', cursor: 'pointer', textAlign: 'left',
                background: isActive ? 'var(--rg-primary-soft)' : 'transparent',
                color: isActive ? 'var(--rg-primary)' : 'var(--rg-text-secondary)',
                fontWeight: isActive ? 600 : 400, fontSize: 13,
              }}
            >
              <Icon size={15} /> {sec.label}
            </button>
          );
        })}
      </div>

      {/* Main content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 32 }}>
        <div ref={errorRef} />
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
        {publishBlock && (
          <PublishBlockedPanel
            block={publishBlock}
            saving={saving}
            onConfirmNoRedFlags={() => {
              setNoRedFlagsConfirmed(true);
              if (draft?.status === PACK_STATUS.ACTIVE) {
                handlePublishNewVersion({ confirmNoRedFlags: true });
              } else {
                handlePublish({ confirmNoRedFlags: true });
              }
            }}
            onDismiss={() => setPublishBlock(null)}
          />
        )}
        {showVersionConfirm && (
          <div style={{ background: 'var(--rg-primary-soft)', border: '1px solid var(--rg-primary)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
            <h4 style={{ fontSize: 14, fontWeight: 700, color: 'var(--rg-text-primary)', display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 8px 0' }}>
              <CheckCircle size={15} /> Publish a new version?
            </h4>
            <p style={{ fontSize: 13, color: 'var(--rg-text-secondary)', lineHeight: 1.5, marginBottom: 12 }}>
              This creates <strong>v{newVersionPreview}</strong> as the active Pack and archives <strong>v{currentActiveVersion}</strong>. Existing referrals keep the version they were created under.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={doPublishNewVersion} disabled={saving} style={saving ? btnDisabled : btnPrimary}>
                <CheckCircle size={14} /> {saving ? 'Publishing…' : `Publish v${newVersionPreview}`}
              </button>
              <button onClick={() => setShowVersionConfirm(false)} disabled={saving} style={btnGhost}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {draft && (
          <SectionRenderer
            section={SECTIONS[activeSection].id}
            draft={draft}
            updateDraft={updateDraft}
            siblingPacks={packs}
            activeFocusTarget={activeFocusTarget}
          />
        )}

        {/* Footer actions */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32, paddingTop: 16, borderTop: '1px solid var(--rg-border-subtle)' }}>
          <button
            onClick={() => setActiveSection((s) => Math.max(0, s - 1))}
            disabled={activeSection === 0}
            style={activeSection === 0 ? btnDisabled : btnGhost}
          >
            <ChevronLeft size={14} /> Previous
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            {draft?.status === PACK_STATUS.DRAFT && (
              <button onClick={handleSave} disabled={saving || !dirty} style={saving || !dirty ? btnDisabled : btnSecondary}>
                <Save size={14} /> {saving ? 'Saving...' : 'Save Draft'}
              </button>
            )}
            {activeSection === SECTIONS.length - 1 && draft?.status === PACK_STATUS.DRAFT && (
              <button onClick={() => handlePublish()} disabled={saving} style={saving ? btnDisabled : btnPrimary}>
                <CheckCircle size={14} /> Publish Pack
              </button>
            )}
            {activeSection === SECTIONS.length - 1 && draft?.status === PACK_STATUS.ACTIVE && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12, color: 'var(--rg-text-secondary)' }}>
                  Current v{currentActiveVersion} → New v{newVersionPreview}
                </span>
                <button onClick={() => handlePublishNewVersion()} disabled={saving} style={saving ? btnDisabled : btnPrimary}>
                  <CheckCircle size={14} /> {saving ? 'Publishing…' : 'Publish new version'}
                </button>
              </div>
            )}
            {activeSection < SECTIONS.length - 1 && (
              <button onClick={() => setActiveSection((s) => s + 1)} style={btnPrimary}>
                Next <ChevronRight size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SECTION RENDERER
// ============================================================================

function SectionRenderer({ section, draft, updateDraft, siblingPacks, activeFocusTarget }) {
  switch (section) {
    case 'metadata': return <MetadataSection draft={draft} updateDraft={updateDraft} activeFocusTarget={activeFocusTarget} />;
    case 'scope': return <ScopeSection draft={draft} updateDraft={updateDraft} activeFocusTarget={activeFocusTarget} />;
    case 'redirectFlags': return <RedirectFlagsSection draft={draft} updateDraft={updateDraft} siblingPacks={siblingPacks} activeFocusTarget={activeFocusTarget} />;
    case 'redFlags': return <RedFlagsSection draft={draft} updateDraft={updateDraft} activeFocusTarget={activeFocusTarget} />;
    case 'fields': return <FieldsSection draft={draft} updateDraft={updateDraft} activeFocusTarget={activeFocusTarget} />;
    case 'attachments': return <AttachmentsSection draft={draft} updateDraft={updateDraft} activeFocusTarget={activeFocusTarget} />;
    case 'notYetSteps': return <NotYetStepsSection draft={draft} updateDraft={updateDraft} activeFocusTarget={activeFocusTarget} />;
    case 'patientMessages': return <PatientMessagesSection draft={draft} updateDraft={updateDraft} activeFocusTarget={activeFocusTarget} />;
    case 'review': return <ReviewSection draft={draft} siblingPacks={siblingPacks} activeFocusTarget={activeFocusTarget} />;
    default: return null;
  }
}

// ============================================================================
// SECTION 1: METADATA
// ============================================================================

function MetadataSection({ draft, updateDraft, activeFocusTarget }) {
  return (
    <div>
      <SectionHeader title="Pack Setup" description="Name, description, and version for this Pack." helpKey="metadata" activeFocusTarget={activeFocusTarget} />
      <FormGroup label="Pack Name">
        <input
          type="text"
          value={draft.name || ''}
          onChange={(e) => updateDraft('name', e.target.value)}
          style={inputStyle}
          placeholder="e.g. Knee Pain Pathway"
        />
      </FormGroup>
      <FormGroup label="Description">
        <textarea
          value={draft.description || ''}
          onChange={(e) => updateDraft('description', e.target.value)}
          style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
          placeholder="Explain what this pathway evaluates and when the referring provider should use it."
        />
      </FormGroup>
      <FormGroup label="Version">
        <input
          type="text"
          value={draft.versionId || '1.0.0'}
          onChange={(e) => updateDraft('versionId', e.target.value)}
          style={inputStyle}
          placeholder="1.0.0"
        />
      </FormGroup>
    </div>
  );
}

// ============================================================================
// SECTION 2: REFERRAL CONTEXT & ESCALATION
// ============================================================================

function ScopeSection({ draft, updateDraft, activeFocusTarget }) {
  const escalation = draft.escalationOptions || [];

  const addOption = () => updateDraft('escalationOptions', [...escalation, '']);
  const removeOption = (idx) => updateDraft('escalationOptions', escalation.filter((_, i) => i !== idx));
  const updateOption = (idx, val) => {
    const updated = [...escalation];
    updated[idx] = val;
    updateDraft('escalationOptions', updated);
  };

  return (
    <div>
      <SectionHeader title="Referral Context & Escalation" description="Visit Information is included in every Pack by default. Define escalation reasons below." helpKey="scope" activeFocusTarget={activeFocusTarget} />
      <FormGroup label="Escalation Reasons">
        <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>
          Enum-only reasons a referring provider can give when requesting faster review or escalation. No free text.
        </p>
        {escalation.map((opt, idx) => (
          <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
            <input
              type="text"
              value={opt}
              onChange={(e) => updateOption(idx, e.target.value)}
              style={{ ...inputStyle, flex: 1 }}
              placeholder="Reason for escalation"
            />
            <button onClick={() => removeOption(idx)} style={btnIcon}><Trash2 size={14} /></button>
          </div>
        ))}
        <button onClick={addOption} style={btnGhost}><Plus size={14} /> Add Reason</button>
      </FormGroup>
    </div>
  );
}

// ============================================================================
// SECTION 3: REDIRECT FLAGS
// ============================================================================

function RedirectFlagsSection({ draft, updateDraft, siblingPacks, activeFocusTarget }) {
  const intra = draft.intraSpecialtyRedirectFlags || [];
  const cross = draft.crossSpecialtyRedirectFlags || [];

  const otherPacks = (siblingPacks || []).filter((p) => p.id !== draft.id);

  const addIntra = () => {
    updateDraft('intraSpecialtyRedirectFlags', [...intra, { ...EMPTY_REDIRECT, id: `rdf_intra_${Date.now()}`, target: { type: 'pack', targetId: '', label: '' } }]);
  };
  const addCross = () => {
    updateDraft('crossSpecialtyRedirectFlags', [...cross, { ...EMPTY_REDIRECT, id: `rdf_cross_${Date.now()}`, target: { type: 'specialty', targetId: '', label: '' } }]);
  };

  const updateIntra = (idx, updates) => {
    const updated = intra.map((item, i) => i === idx ? { ...item, ...updates } : item);
    updateDraft('intraSpecialtyRedirectFlags', updated);
  };
  const updateCross = (idx, updates) => {
    const updated = cross.map((item, i) => i === idx ? { ...item, ...updates } : item);
    updateDraft('crossSpecialtyRedirectFlags', updated);
  };

  const removeIntra = (idx) => updateDraft('intraSpecialtyRedirectFlags', intra.filter((_, i) => i !== idx));
  const removeCross = (idx) => updateDraft('crossSpecialtyRedirectFlags', cross.filter((_, i) => i !== idx));

  return (
    <div>
      <SectionHeader title="Redirect Flags" description="Pre-outcome routing checks. If matched, referral is redirected — no form written." helpKey="redirectFlags" activeFocusTarget={activeFocusTarget} />

      <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, color: 'var(--rg-text-primary)' }}>Intra-Specialty (same specialty, different pack)</h4>
      {otherPacks.length === 0 && intra.length === 0 && (
        <div style={{ ...cardStyle, color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 24 }}>
          No other packs exist yet. You can add intra-specialty redirects later after creating additional packs.
        </div>
      )}
      {intra.map((flag, idx) => (
        <IntraRedirectFlagEditor
          key={flag.id || idx}
          flag={flag}
          otherPacks={otherPacks}
          isDraft={draft.status === 'draft' || !draft.status}
          onChange={(u) => updateIntra(idx, u)}
          onRemove={() => removeIntra(idx)}
        />
      ))}
      {otherPacks.length > 0 && (
        <button onClick={addIntra} style={{ ...btnGhost, marginBottom: 24 }}><Plus size={14} /> Add Intra-Specialty Redirect</button>
      )}
      {otherPacks.length === 0 && intra.length > 0 && (
        <div style={{ fontSize: 12, color: '#fbbf24', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 6 }}>
          <AlertTriangle size={12} /> No other packs exist yet — target links cannot be resolved until additional packs are created.
        </div>
      )}

      <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, color: 'var(--rg-text-primary)' }}>Cross-Specialty (different specialty)</h4>
      {cross.map((flag, idx) => (
        <CrossRedirectFlagEditor key={flag.id || idx} flag={flag} onChange={(u) => updateCross(idx, u)} onRemove={() => removeCross(idx)} />
      ))}
      <button onClick={addCross} style={btnGhost}><Plus size={14} /> Add Cross-Specialty Redirect</button>
    </div>
  );
}

function IntraRedirectFlagEditor({ flag, otherPacks, isDraft, onChange, onRemove }) {
  const hasTarget = flag.target?.targetId;
  const isUnresolved = flag.target?.label && !flag.target?.targetId;

  const handlePackSelect = (e) => {
    const packId = e.target.value;
    if (!packId) {
      onChange({ target: { type: 'pack', targetId: '', label: flag.target?.label || '' } });
      return;
    }
    const selected = otherPacks.find((p) => p.id === packId);
    if (selected) {
      const displayName = selected.name || selected.pack?.metadata?.name || 'Untitled';
      onChange({ target: { type: 'pack', targetId: selected.id, label: displayName } });
    }
  };

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <GripVertical size={14} style={{ color: '#64748b' }} />
        <button onClick={onRemove} style={btnIcon}><Trash2 size={14} /></button>
      </div>
      <FormGroup label="Label (when to redirect)" compact>
        <input type="text" value={flag.label} onChange={(e) => onChange({ label: e.target.value })} style={inputStyle} placeholder="When to redirect" />
      </FormGroup>
      <FormGroup label="Redirect Reason" compact>
        <input type="text" value={flag.redirectReason || ''} onChange={(e) => onChange({ redirectReason: e.target.value })} style={inputStyle} placeholder="Why this redirects" />
      </FormGroup>
      <FormGroup label="Target Pack" compact>
        {otherPacks.length > 0 ? (
          <select value={flag.target?.targetId || ''} onChange={handlePackSelect} style={inputStyle}>
            <option value="">— Select target pack —</option>
            {otherPacks.map((p) => {
              const name = p.name || p.pack?.metadata?.name || 'Untitled';
              const version = p.versionId || p.pack?.metadata?.version || '1.0.0';
              const status = p.status || 'draft';
              return (
                <option key={p.id} value={p.id}>
                  {name} (v{version}, {status})
                </option>
              );
            })}
          </select>
        ) : (
          <input
            type="text"
            value={flag.target?.label || ''}
            onChange={(e) => onChange({ target: { ...flag.target, type: 'pack', label: e.target.value } })}
            style={inputStyle}
            placeholder="Planned target pack name (link later)"
          />
        )}
      </FormGroup>
      {isDraft && isUnresolved && (
        <div style={{ fontSize: 12, color: '#fbbf24', display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
          <AlertTriangle size={12} /> Redirect target is planned but not linked to an existing pack.
        </div>
      )}
      {hasTarget && (
        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
          Target ID: <span style={{ fontFamily: 'monospace', color: '#cbd5e1' }}>{flag.target.targetId}</span>
        </div>
      )}
    </div>
  );
}

function CrossRedirectFlagEditor({ flag, onChange, onRemove }) {
  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <GripVertical size={14} style={{ color: '#64748b' }} />
        <button onClick={onRemove} style={btnIcon}><Trash2 size={14} /></button>
      </div>
      <FormGroup label="Label (when to redirect)" compact>
        <input type="text" value={flag.label} onChange={(e) => onChange({ label: e.target.value })} style={inputStyle} placeholder="When to redirect" />
      </FormGroup>
      <FormGroup label="Redirect Reason" compact>
        <input type="text" value={flag.redirectReason || ''} onChange={(e) => onChange({ redirectReason: e.target.value })} style={inputStyle} placeholder="Why this redirects" />
      </FormGroup>
      <FormGroup label="Target Specialty Label" compact>
        <input type="text" value={flag.target?.label || ''} onChange={(e) => onChange({ target: { ...flag.target, label: e.target.value } })} style={inputStyle} placeholder="Target specialty name" />
      </FormGroup>
      <FormGroup label="Target Specialty ID" compact>
        <input type="text" value={flag.target?.targetId || ''} onChange={(e) => onChange({ target: { ...flag.target, targetId: e.target.value } })} style={inputStyle} placeholder="Target specialty document ID" />
      </FormGroup>
    </div>
  );
}

// ============================================================================
// SECTION 4: RED FLAGS
// ============================================================================

function RedFlagsSection({ draft, updateDraft, activeFocusTarget }) {
  const redFlags = draft.redFlags || [];

  const addFlag = () => {
    updateDraft('redFlags', [...redFlags, { ...EMPTY_RED_FLAG, id: `rf_${Date.now()}` }]);
  };
  const updateFlag = (idx, updates) => {
    const updated = redFlags.map((item, i) => i === idx ? { ...item, ...updates } : item);
    updateDraft('redFlags', updated);
  };
  const removeFlag = (idx) => updateDraft('redFlags', redFlags.filter((_, i) => i !== idx));
  // Evidence Assist (Phase 2C-1) — Never-PHI; positive_only, never auto-selects.
  const setFlagEvidence = (idx, key, val) => {
    const flag = redFlags[idx];
    // Manual edit → stamp MANUAL + reviewed so background auto-linking never overwrites it.
    const evidenceMap = { ...(flag.evidenceMap || {}), [key]: val, conceptSource: 'MANUAL', reviewedBySpecialist: true };
    updateFlag(idx, { evidenceMap });
  };

  return (
    <div>
      <SectionHeader title="Red Flags" description="Conditions that force URGENT outcome. Evaluated after redirect checks." helpKey="redFlags" activeFocusTarget={activeFocusTarget} />
      {redFlags.map((flag, idx) => (
        <div key={flag.id || idx} style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>Red Flag #{idx + 1}</span>
            <button onClick={() => removeFlag(idx)} style={btnIcon}><Trash2 size={14} /></button>
          </div>
          <FormGroup label="Label" compact>
            <input type="text" value={flag.label} onChange={(e) => updateFlag(idx, { label: e.target.value })} style={inputStyle} placeholder="Red flag description" />
          </FormGroup>
          <FormGroup label="Description" compact>
            <input type="text" value={flag.description || ''} onChange={(e) => updateFlag(idx, { description: e.target.value })} style={inputStyle} placeholder="Additional context" />
          </FormGroup>
          <FormGroup label="Severity" compact>
            <select value={flag.severity || 'high'} onChange={(e) => updateFlag(idx, { severity: e.target.value })} style={inputStyle}>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </FormGroup>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={flag.forceUrgent !== false} onChange={(e) => updateFlag(idx, { forceUrgent: e.target.checked })} />
            Force URGENT outcome
          </label>
          <EvidenceAssistPanel
            evidenceMap={flag.evidenceMap}
            subtitle="Advanced/optional. Prepared automatically in the background. This will never auto-select a red flag — only explicit positive evidence creates a chip; negative and uncertainty phrases suppress it. No patient data here."
            onSuggest={() => draftRedFlagEvidenceMap(flag)}
            onApplySuggestion={(evidenceMap) => updateFlag(idx, { evidenceMap })}
            hasExistingEvidence={evidenceMapHasValues(flag.evidenceMap)}
          >
            <EvidenceRow label="Positive phrases (one per line)">
              <ListInput multiline value={flag.evidenceMap?.positivePhrases} onChange={(v) => setFlagEvidence(idx, 'positivePhrases', v)} placeholder="active GI bleeding, melena, hematemesis" />
            </EvidenceRow>
            <EvidenceRow label="Negative phrases (one per line)">
              <ListInput multiline value={flag.evidenceMap?.negativePhrases} onChange={(v) => setFlagEvidence(idx, 'negativePhrases', v)} placeholder="denies bleeding, no active bleeding" />
            </EvidenceRow>
            <EvidenceRow label="Uncertainty phrases (one per line)">
              <ListInput multiline value={flag.evidenceMap?.uncertaintyPhrases} onChange={(v) => setFlagEvidence(idx, 'uncertaintyPhrases', v)} placeholder="rule out bleeding" />
            </EvidenceRow>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--rg-text-muted)' }}>
              <Lock size={11} /> Mode: {RED_FLAG_EVIDENCE_MODE} · requires explicit positive evidence (locked for safety)
            </div>
          </EvidenceAssistPanel>
        </div>
      ))}
      <button onClick={addFlag} style={btnGhost}><Plus size={14} /> Add Red Flag</button>
    </div>
  );
}

// ============================================================================
// SECTION 5: CLINICAL FIELDS
// ============================================================================

function generateStepIdFromField(fieldId) {
  return `provide_${fieldId}`;
}

function syncFieldNotYetStep(field, steps) {
  const stepId = generateStepIdFromField(field.id);
  const existingIdx = steps.findIndex((s) => s.id === stepId);
  if (field.required && field.affectsOutcome && field.notYetInstruction) {
    const step = {
      id: stepId,
      label: `Provide ${field.label || field.id}`,
      description: field.notYetInstruction,
      linkedFieldIds: [field.id],
      requiredArtifacts: [{ type: 'field', refId: field.id }],
    };
    if (existingIdx >= 0) {
      return steps.map((s, i) => (i === existingIdx ? step : s));
    }
    return [...steps, step];
  }
  if (existingIdx >= 0) {
    return steps.filter((_, i) => i !== existingIdx);
  }
  return steps;
}

function FieldsSection({ draft, updateDraft, activeFocusTarget }) {
  const fields = draft.fields || [];

  const addField = () => {
    updateDraft('fields', [...fields, { ...EMPTY_FIELD, id: `field_${Date.now()}` }]);
  };

  const updateFieldAndSyncSteps = (idx, updates) => {
    const updatedFields = fields.map((item, i) => (i === idx ? { ...item, ...updates } : item));
    const updatedField = updatedFields[idx];
    let steps = draft.notYetNextSteps || [];

    if ('required' in updates || 'affectsOutcome' in updates || 'notYetInstruction' in updates) {
      steps = syncFieldNotYetStep(updatedField, steps);
      const stepId = generateStepIdFromField(updatedField.id);
      const hasStep = steps.some((s) => s.id === stepId);
      updatedFields[idx] = {
        ...updatedField,
        notYetStepIds: hasStep ? [stepId] : [],
      };
    }

    updateDraft('fields', updatedFields);
    if ('required' in updates || 'affectsOutcome' in updates || 'notYetInstruction' in updates) {
      updateDraft('notYetNextSteps', steps);
    }
  };

  const handleTypeChange = (idx, newType) => {
    const updates = { type: newType };
    if (newType === 'slider') {
      const field = fields[idx];
      if (field.min == null) updates.min = 0;
      if (field.max == null) updates.max = 10;
      if (field.step == null) updates.step = 1;
      if (!field.anchors || Object.keys(field.anchors).length === 0) updates.anchors = { '0': 'Low concern', '5': 'Moderate concern', '10': 'High concern' };
      if (!field.dataType) updates.dataType = 'ordinal_scale';
    }
    updateFieldAndSyncSteps(idx, updates);
  };

  const handleRequiredToggle = (idx, checked) => {
    if (checked) {
      updateFieldAndSyncSteps(idx, { required: true, affectsOutcome: true });
    } else {
      updateFieldAndSyncSteps(idx, { required: false, affectsOutcome: false, notYetInstruction: '', notYetStepIds: [] });
    }
  };

  const removeField = (idx) => {
    const field = fields[idx];
    const stepId = generateStepIdFromField(field.id);
    const steps = (draft.notYetNextSteps || []).filter((s) => s.id !== stepId);
    updateDraft('fields', fields.filter((_, i) => i !== idx));
    updateDraft('notYetNextSteps', steps);
  };

  // Evidence Assist (Phase 2C-1) — Never-PHI. Merge a single key into the
  // field's evidenceMap. Does not touch Not-Yet step sync.
  const setFieldEvidence = (idx, key, val) => {
    const field = fields[idx];
    // Manual edit → stamp MANUAL + reviewed so background auto-linking never overwrites it.
    const evidenceMap = { ...(field.evidenceMap || {}), [key]: val, conceptSource: 'MANUAL', reviewedBySpecialist: true };
    updateFieldAndSyncSteps(idx, { evidenceMap });
  };
  const setOptionEvidenceMap = (idx, optionEvidenceMap) => {
    updateFieldAndSyncSteps(idx, { optionEvidenceMap });
  };

  return (
    <div>
      <SectionHeader title="Clinical Fields" description="Clinical facts the referring provider must provide. Required clinical fields can produce a Not-Yet outcome if missing." helpKey="fields" activeFocusTarget={activeFocusTarget} />
      {fields.map((field, idx) => (
        <div key={field.id || idx} style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#cbd5e1' }}>Field #{idx + 1}</span>
            <button onClick={() => removeField(idx)} style={btnIcon}><Trash2 size={14} /></button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormGroup label="Label" compact>
              <input type="text" value={field.label} onChange={(e) => updateFieldAndSyncSteps(idx, { label: e.target.value })} style={inputStyle} placeholder="Field label" />
            </FormGroup>
            <FormGroup label="Type" compact>
              <select value={field.type} onChange={(e) => handleTypeChange(idx, e.target.value)} style={inputStyle}>
                {FIELD_TYPE_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </FormGroup>
          </div>
          <FormGroup label="Help Text" compact>
            <input type="text" value={field.helpText || ''} onChange={(e) => updateFieldAndSyncSteps(idx, { helpText: e.target.value })} style={inputStyle} placeholder="Optional guidance for the referring provider" />
          </FormGroup>
          <FieldEvidenceAssist
            field={field}
            onChange={(key, val) => setFieldEvidence(idx, key, val)}
            onApplyDraft={(evidenceMap) => updateFieldAndSyncSteps(idx, { evidenceMap })}
          />
          {['select', 'multiselect', 'radio', 'checkbox'].includes(field.type) && (
            <OptionsEditor
              options={field.options || []}
              optionEvidenceMap={field.optionEvidenceMap || {}}
              onChange={(opts) => updateFieldAndSyncSteps(idx, { options: opts })}
              onEvidenceChange={(oem) => setOptionEvidenceMap(idx, oem)}
            />
          )}
          {field.type === 'slider' && (
            <SliderConfigEditor
              field={field}
              onChange={(updates) => updateFieldAndSyncSteps(idx, updates)}
            />
          )}
          <div style={{ marginTop: 10, padding: 12, background: 'var(--rg-bg-muted)', borderRadius: 8, border: '1px solid var(--rg-border-subtle)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', color: 'var(--rg-text-primary)' }}>
              <input type="checkbox" checked={!!field.required && !!field.affectsOutcome} onChange={(e) => handleRequiredToggle(idx, e.target.checked)} />
              <strong>Required for Routine outcome</strong>
            </label>
            <p style={{ fontSize: 11, color: 'var(--rg-text-secondary)', margin: '4px 0 0 26px' }}>
              If missing, this item will block Routine and produce a Not-Yet instruction.
            </p>
            {field.required && field.affectsOutcome && (
              <div style={{ marginTop: 10, marginLeft: 26 }}>
                <FormGroup label="Not-Yet instruction if missing" compact>
                  <textarea
                    value={field.notYetInstruction || ''}
                    onChange={(e) => updateFieldAndSyncSteps(idx, { notYetInstruction: e.target.value })}
                    style={{ ...inputStyle, minHeight: 50, resize: 'vertical' }}
                    placeholder="What should the referring provider provide if this item is missing?"
                  />
                </FormGroup>
                {!field.notYetInstruction && (
                  <p style={{ fontSize: 11, color: '#fbbf24', margin: '4px 0 0 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <AlertTriangle size={11} /> Required field is missing a Not-Yet recovery instruction.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
      <button onClick={addField} style={btnGhost}><Plus size={14} /> Add Field</button>
    </div>
  );
}

function OptionsEditor({ options, optionEvidenceMap = {}, onChange, onEvidenceChange }) {
  const addOpt = () => onChange([...options, { value: '', label: '' }]);
  const removeOpt = (idx) => {
    const removed = options[idx]?.value;
    onChange(options.filter((_, i) => i !== idx));
    // Drop any orphaned option-evidence key for the removed option.
    if (onEvidenceChange && removed && optionEvidenceMap[removed]) {
      const next = { ...optionEvidenceMap };
      delete next[removed];
      onEvidenceChange(next);
    }
  };
  // Label and value move together (existing behavior). If the value changes and
  // it had option evidence, migrate the evidence to the new key.
  const updateOptValue = (idx, val) => {
    const prevValue = options[idx]?.value;
    const updated = options.map((o, i) => i === idx ? { ...o, value: val, label: val } : o);
    onChange(updated);
    if (onEvidenceChange && prevValue && prevValue !== val && optionEvidenceMap[prevValue]) {
      const next = { ...optionEvidenceMap };
      next[val] = next[prevValue];
      delete next[prevValue];
      onEvidenceChange(next);
    }
  };
  const setOptEvidence = (optValue, key, val) => {
    if (!optValue || !onEvidenceChange) return;
    const existing = optionEvidenceMap[optValue] || {};
    // Manual edit → stamp MANUAL + reviewed so background auto-linking never overwrites it.
    onEvidenceChange({ ...optionEvidenceMap, [optValue]: { ...existing, [key]: val, conceptSource: 'MANUAL', reviewedBySpecialist: true } });
  };

  return (
    <div style={{ marginLeft: 16, paddingLeft: 12, borderLeft: '2px solid rgba(148,163,184,0.2)' }}>
      <p style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 4 }}>Options</p>
      {options.map((opt, idx) => {
        const optValue = opt.value || '';
        const optEvidence = optValue ? (optionEvidenceMap[optValue] || {}) : {};
        return (
          <div key={idx} style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
              <input type="text" value={opt.label || opt.value || ''} onChange={(e) => updateOptValue(idx, e.target.value)} style={{ ...inputStyle, flex: 1, fontSize: 12, padding: '4px 8px' }} placeholder="Option label" />
              <button onClick={() => removeOpt(idx)} style={btnIcon}><X size={12} /></button>
            </div>
            {optValue ? (
              <EvidenceAssistPanel
                evidenceMap={optEvidence}
                subtitle="Advanced/optional. Prepared automatically in the background. Keywords that point to this option — evidence only, never auto-selects an answer. No patient data here."
                onSuggest={() => draftOptionEvidence(optValue)}
                onApplySuggestion={(ev) => onEvidenceChange?.({ ...optionEvidenceMap, [optValue]: ev })}
                hasExistingEvidence={evidenceMapHasValues(optEvidence)}
              >
                <EvidenceRow label="Aliases (comma or new line)">
                  <ListInput value={optEvidence.aliases} onChange={(v) => setOptEvidence(optValue, 'aliases', v)} placeholder="leukopenia, low WBC, low white count, neutropenia" />
                </EvidenceRow>
                <EvidenceRow label="Phrases (one per line)">
                  <ListInput multiline value={optEvidence.phrases} onChange={(v) => setOptEvidence(optValue, 'phrases', v)} placeholder="e.g. white count below normal" />
                </EvidenceRow>
                <EvidenceRow label="Negative phrases (one per line)">
                  <ListInput multiline value={optEvidence.negativePhrases} onChange={(v) => setOptEvidence(optValue, 'negativePhrases', v)} placeholder="suppress a false match" />
                </EvidenceRow>
                <EvidenceRow label="Uncertainty phrases (one per line)">
                  <ListInput multiline value={optEvidence.uncertaintyPhrases} onChange={(v) => setOptEvidence(optValue, 'uncertaintyPhrases', v)} placeholder="mark evidence as uncertain" />
                </EvidenceRow>
              </EvidenceAssistPanel>
            ) : (
              <p style={{ fontSize: 10, color: 'var(--rg-text-muted)', marginLeft: 2 }}>Enter an option label to add evidence aliases.</p>
            )}
          </div>
        );
      })}
      <button onClick={addOpt} style={{ ...btnGhost, fontSize: 11 }}><Plus size={12} /> Option</button>
    </div>
  );
}

function SliderConfigEditor({ field, onChange }) {
  const min = field.min ?? 0;
  const max = field.max ?? 10;
  const step = field.step ?? 1;
  const anchors = field.anchors || {};
  const mid = Math.round((min + max) / 2);

  const updateAnchor = (key, val) => {
    onChange({ anchors: { ...anchors, [key]: val } });
  };

  const handleMinChange = (newMin) => {
    const n = Number(newMin);
    if (isNaN(n)) return;
    const newAnchors = { ...anchors };
    const oldMin = String(min);
    if (newAnchors[oldMin] !== undefined) {
      const label = newAnchors[oldMin];
      delete newAnchors[oldMin];
      newAnchors[String(n)] = label;
    }
    const newMid = String(Math.round((n + max) / 2));
    const oldMid = String(mid);
    if (oldMid !== newMid && newAnchors[oldMid] !== undefined) {
      const label = newAnchors[oldMid];
      delete newAnchors[oldMid];
      newAnchors[newMid] = label;
    }
    onChange({ min: n, anchors: newAnchors });
  };

  const handleMaxChange = (newMax) => {
    const n = Number(newMax);
    if (isNaN(n)) return;
    const newAnchors = { ...anchors };
    const oldMax = String(max);
    if (newAnchors[oldMax] !== undefined) {
      const label = newAnchors[oldMax];
      delete newAnchors[oldMax];
      newAnchors[String(n)] = label;
    }
    const newMid = String(Math.round((min + n) / 2));
    const oldMid = String(mid);
    if (oldMid !== newMid && newAnchors[oldMid] !== undefined) {
      const label = newAnchors[oldMid];
      delete newAnchors[oldMid];
      newAnchors[newMid] = label;
    }
    onChange({ max: n, anchors: newAnchors });
  };

  return (
    <div style={{ marginLeft: 16, paddingLeft: 12, borderLeft: '2px solid rgba(148,163,184,0.2)', marginTop: 8 }}>
      <p style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 8 }}>Slider Configuration</p>
      <p style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>
        Use for ordinal/subjective ratings (concern level, pain, symptom burden). Not for exact measurements.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
        <FormGroup label="Min value" compact>
          <input type="number" value={min} onChange={(e) => handleMinChange(e.target.value)} style={{ ...inputStyle, fontSize: 12, padding: '4px 8px' }} />
        </FormGroup>
        <FormGroup label="Max value" compact>
          <input type="number" value={max} onChange={(e) => handleMaxChange(e.target.value)} style={{ ...inputStyle, fontSize: 12, padding: '4px 8px' }} />
        </FormGroup>
        <FormGroup label="Step" compact>
          <input type="number" value={step} min={0.01} onChange={(e) => { const v = Number(e.target.value); if (!isNaN(v)) onChange({ step: v }); }} style={{ ...inputStyle, fontSize: 12, padding: '4px 8px' }} />
        </FormGroup>
      </div>
      <p style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 6 }}>Anchor Labels</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, color: '#64748b', width: 60, flexShrink: 0 }}>{min} (min)</span>
          <input type="text" value={anchors[String(min)] || ''} onChange={(e) => updateAnchor(String(min), e.target.value)} style={{ ...inputStyle, flex: 1, fontSize: 12, padding: '4px 8px' }} placeholder="e.g. Low concern" />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, color: '#64748b', width: 60, flexShrink: 0 }}>{mid} (mid)</span>
          <input type="text" value={anchors[String(mid)] || ''} onChange={(e) => updateAnchor(String(mid), e.target.value)} style={{ ...inputStyle, flex: 1, fontSize: 12, padding: '4px 8px' }} placeholder="e.g. Moderate concern" />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, color: '#64748b', width: 60, flexShrink: 0 }}>{max} (max)</span>
          <input type="text" value={anchors[String(max)] || ''} onChange={(e) => updateAnchor(String(max), e.target.value)} style={{ ...inputStyle, flex: 1, fontSize: 12, padding: '4px 8px' }} placeholder="e.g. High concern" />
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SECTION 6: ATTACHMENT REQUIREMENTS
// ============================================================================

function normalizeAttachment(att) {
  const normalized = { ...att };
  if (normalized.requiredForIntakeCompleteness == null && normalized.required != null) {
    normalized.requiredForIntakeCompleteness = normalized.required;
  }
  delete normalized.required;
  return normalized;
}

function AttachmentsSection({ draft, updateDraft, activeFocusTarget }) {
  const rawAttachments = draft.attachmentRequirements || [];
  const attachments = rawAttachments.map(normalizeAttachment);

  const addAttachment = () => {
    updateDraft('attachmentRequirements', [...attachments, {
      id: `att_${Date.now()}`,
      label: '',
      attachmentType: '',
      requiredForIntakeCompleteness: true,
      description: '',
    }]);
  };
  const updateAttachment = (idx, updates) => {
    const updated = attachments.map((item, i) => i === idx ? { ...item, ...updates } : item);
    updateDraft('attachmentRequirements', updated);
  };
  const removeAttachment = (idx) => updateDraft('attachmentRequirements', attachments.filter((_, i) => i !== idx));
  // Evidence Assist (Phase 2C-1) — Never-PHI; documentTypeHints use the canonical
  // evidence document-type enum. Never auto-attaches a document.
  const setAttEvidence = (idx, key, val) => {
    const att = attachments[idx];
    // Manual edit → stamp MANUAL + reviewed so background auto-linking never overwrites it.
    const evidenceMap = { ...(att.evidenceMap || {}), [key]: val, conceptSource: 'MANUAL', reviewedBySpecialist: true };
    updateAttachment(idx, { evidenceMap });
  };
  const toggleDocTypeHint = (idx, type) => {
    const current = attachments[idx]?.evidenceMap?.documentTypeHints || [];
    const next = current.includes(type) ? current.filter((t) => t !== type) : [...current, type];
    setAttEvidence(idx, 'documentTypeHints', next);
  };

  return (
    <div>
      <SectionHeader
        title="Attachment Requirements"
        description="Documents or files expected with this referral. Required items are checked by intake and can create Needs Info rework if missing."
        helpKey="attachments"
        activeFocusTarget={activeFocusTarget}
      />
      {attachments.map((att, idx) => {
        const isRequired = !!att.requiredForIntakeCompleteness;
        return (
          <div key={att.id || idx} style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>Attachment #{idx + 1}</span>
              <button onClick={() => removeAttachment(idx)} style={btnIcon}><Trash2 size={14} /></button>
            </div>
            <FormGroup label="Label" compact>
              <input type="text" value={att.label} onChange={(e) => updateAttachment(idx, { label: e.target.value })} style={inputStyle} placeholder="e.g. MRI Report" />
            </FormGroup>
            <FormGroup label="Attachment Type" compact>
              <select value={att.attachmentType || ''} onChange={(e) => updateAttachment(idx, { attachmentType: e.target.value })} style={inputStyle}>
                <option value="" disabled>Select type…</option>
                {Object.entries(ATTACHMENT_TYPE).map(([key, val]) => (
                  <option key={key} value={val}>{ATTACHMENT_TYPE_LABELS[key]}</option>
                ))}
              </select>
            </FormGroup>
            <FormGroup label="Description" compact>
              <input type="text" value={att.description || ''} onChange={(e) => updateAttachment(idx, { description: e.target.value })} style={inputStyle} placeholder="What should this document contain?" />
            </FormGroup>
            <div style={{ marginTop: 4 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isRequired}
                  onChange={(e) => updateAttachment(idx, { requiredForIntakeCompleteness: e.target.checked })}
                />
                Required for intake completeness
              </label>
              <p style={{ fontSize: 11, color: '#94a3b8', margin: '4px 0 0 24px', lineHeight: 1.4 }}>
                {isRequired
                  ? 'If missing, intake will request this item and the referral will move to Needs Info before scheduling. This does not change the Gate outcome.'
                  : 'Optional attachment. Referral can proceed without this document unless intake requests it later.'}
              </p>
            </div>
            <EvidenceAssistPanel
              evidenceMap={att.evidenceMap}
              subtitle="Advanced/optional. Prepared automatically in the background. Helps later evidence search point to the right document. Never auto-attaches a file. No patient data here."
              onSuggest={() => draftAttachmentEvidenceMap(att)}
              onApplySuggestion={(evidenceMap) => updateAttachment(idx, { evidenceMap })}
              hasExistingEvidence={evidenceMapHasValues(att.evidenceMap)}
            >
              <EvidenceRow label="Document type hints">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {EVIDENCE_DOCUMENT_TYPES.map((type) => {
                    const selected = (att.evidenceMap?.documentTypeHints || []).includes(type);
                    return (
                      <button
                        type="button"
                        key={type}
                        onClick={() => toggleDocTypeHint(idx, type)}
                        style={{
                          fontSize: 11, padding: '3px 8px', borderRadius: 999, cursor: 'pointer',
                          border: selected ? '1px solid #6366f1' : '1px solid var(--rg-border-subtle)',
                          background: selected ? 'rgba(99,102,241,0.18)' : 'transparent',
                          color: selected ? '#a5b4fc' : 'var(--rg-text-secondary)',
                        }}
                      >
                        {EVIDENCE_DOCUMENT_TYPE_LABELS[type]}
                      </button>
                    );
                  })}
                </div>
              </EvidenceRow>
              <EvidenceRow label="Aliases (comma or new line)">
                <ListInput value={att.evidenceMap?.aliases} onChange={(v) => setAttEvidence(idx, 'aliases', v)} placeholder="CBC, complete blood count" />
              </EvidenceRow>
              <EvidenceRow label="Phrases (one per line)">
                <ListInput multiline value={att.evidenceMap?.phrases} onChange={(v) => setAttEvidence(idx, 'phrases', v)} placeholder="e.g. complete blood count with differential" />
              </EvidenceRow>
            </EvidenceAssistPanel>
          </div>
        );
      })}
      <button onClick={addAttachment} style={btnGhost}><Plus size={14} /> Add Attachment Requirement</button>
    </div>
  );
}

// ============================================================================
// SECTION 7: NOT-YET RECOVERY
// ============================================================================

function NotYetStepsSection({ draft, updateDraft, activeFocusTarget }) {
  const steps = draft.notYetNextSteps || [];
  const fields = draft.fields || [];
  const fieldMap = useMemo(() => {
    const m = {};
    fields.forEach((f) => { m[f.id] = f; });
    return m;
  }, [fields]);

  const autoSteps = steps.filter((s) => s.linkedFieldIds?.length > 0);
  const hasRequiredFieldsWithoutSteps = fields.some(
    (f) => f.required && f.affectsOutcome && f.notYetInstruction && !steps.some((s) => s.linkedFieldIds?.includes(f.id))
  );

  const updateStepDescription = (idx, description) => {
    const stepIdx = steps.findIndex((s) => s.id === autoSteps[idx].id);
    if (stepIdx < 0) return;
    const linkedField = autoSteps[idx].linkedFieldIds?.[0];
    const updated = steps.map((s, i) => i === stepIdx ? { ...s, description } : s);
    updateDraft('notYetNextSteps', updated);
    if (linkedField) {
      const fieldIdx = fields.findIndex((f) => f.id === linkedField);
      if (fieldIdx >= 0) {
        const updatedFields = fields.map((f, i) => i === fieldIdx ? { ...f, notYetInstruction: description } : f);
        updateDraft('fields', updatedFields);
      }
    }
  };

  return (
    <div>
      <SectionHeader title="Not-Yet Recovery" description="Auto-generated from required clinical fields. These instructions are shown to the referring provider when required clinical criteria are missing." helpKey="notYetSteps" activeFocusTarget={activeFocusTarget} />
      {autoSteps.length === 0 && (
        <div style={{ ...cardStyle, textAlign: 'center', color: '#94a3b8' }}>
          <p style={{ margin: 0, fontSize: 13 }}>No Not-Yet recovery steps generated yet.</p>
          <p style={{ margin: '8px 0 0', fontSize: 12 }}>
            To generate steps, go to <strong>Clinical Fields</strong>, mark a field as
            "Required for Routine outcome" and add a
            "Not-Yet instruction if missing."
          </p>
        </div>
      )}
      {autoSteps.map((step, idx) => {
        const linkedField = step.linkedFieldIds?.[0];
        const field = linkedField ? fieldMap[linkedField] : null;
        return (
          <div key={step.id} style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>
                {field ? field.label || field.id : step.label}
              </span>
              <span style={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace' }}>{step.id}</span>
            </div>
            <FormGroup label="Recovery instruction" compact>
              <textarea
                value={step.description || ''}
                onChange={(e) => updateStepDescription(idx, e.target.value)}
                style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
                placeholder="What the referring provider needs to do"
              />
            </FormGroup>
            {field && (
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                Linked to field: <strong>{field.label || field.id}</strong> ({field.type})
              </div>
            )}
          </div>
        );
      })}
      {hasRequiredFieldsWithoutSteps && (
        <div style={{ ...cardStyle, borderColor: '#f59e0b', background: 'rgba(245,158,11,0.08)' }}>
          <p style={{ margin: 0, fontSize: 12, color: '#f59e0b' }}>
            <AlertTriangle size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Some required fields are missing auto-generated Not-Yet steps. Check Clinical Fields for fields without a Not-Yet instruction.
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SECTION 8: OUTCOME MESSAGES
// ============================================================================

function PatientMessagesSection({ draft, updateDraft, activeFocusTarget }) {
  const messages = draft.patientMessageTemplates || {
    urgent: 'Your referral has been marked for urgent review because one or more concerning features were identified. The care team will review the referral and determine the next appropriate step.',
    routine: 'Your referral has the required clinical information for routine review. The care team will review the referral and contact you about the next step.',
    not_yet: 'Your referral is not complete yet. Additional clinical information is needed before review can continue. The referring provider will be asked to provide the missing information.',
    redirect: 'This referral appears to belong in a different pathway or specialty. The referring provider will be guided to the correct next step.',
  };

  const updateMessage = (key, val) => {
    updateDraft('patientMessageTemplates', { ...messages, [key]: val });
  };

  const hasRedirectFlags = (draft.intraSpecialtyRedirectFlags || []).length > 0
    || (draft.crossSpecialtyRedirectFlags || []).length > 0;

  return (
    <div>
      <SectionHeader title="Outcome Messages" description="Messages shown for each Gate outcome. Keep language clear, short, non-diagnostic, and non-alarming." helpKey="patientMessages" activeFocusTarget={activeFocusTarget} />
      <FormGroup label="URGENT message">
        <textarea value={messages.urgent || ''} onChange={(e) => updateMessage('urgent', e.target.value)} style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} placeholder="Message when referral is urgent" />
      </FormGroup>
      <FormGroup label="ROUTINE message">
        <textarea value={messages.routine || ''} onChange={(e) => updateMessage('routine', e.target.value)} style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} placeholder="Message when referral is routine" />
      </FormGroup>
      <FormGroup label="NOT-YET message">
        <textarea value={messages.not_yet || ''} onChange={(e) => updateMessage('not_yet', e.target.value)} style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} placeholder="Message when referral is incomplete" />
      </FormGroup>
      {hasRedirectFlags ? (
        <FormGroup label="REDIRECT message">
          <textarea value={messages.redirect || ''} onChange={(e) => updateMessage('redirect', e.target.value)} style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} placeholder="Message when referral is redirected to another pathway" />
        </FormGroup>
      ) : (
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
          REDIRECT message — not applicable (no redirect flags defined in this Pack).
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SECTION 9: REVIEW & PUBLISH
// ============================================================================

function ReviewSection({ draft, siblingPacks, activeFocusTarget }) {
  const fields = draft.fields || [];
  const fieldCount = fields.length;
  const redFlagCount = (draft.redFlags || []).length;
  const redirectCount = (draft.intraSpecialtyRedirectFlags || []).length + (draft.crossSpecialtyRedirectFlags || []).length;
  const attachmentCount = (draft.attachmentRequirements || []).length;
  const steps = draft.notYetNextSteps || [];
  const stepCount = steps.length;
  const stepIds = new Set(steps.map((s) => s.id));
  const fieldIds = new Set(fields.map((f) => f.id));

  const otherPacks = (siblingPacks || []).filter((p) => p.id !== draft.id);
  const otherPackIds = new Set(otherPacks.map((p) => p.id));

  const issues = [];
  // Phase 2: surface the same hard publish-blocking rules the publish gate
  // enforces (name, semver version, ≥1 field, ROUTINE/NOT_YET/URGENT outcome
  // messages, REDIRECT message where applicable) as pre-publish warnings here.
  issues.push(...buildPublishIntegrityIssues(draft));
  if (redFlagCount === 0) issues.push('No red flags defined — add at least one, or confirm "No red flags by design" when you publish');

  fields.forEach((field) => {
    if (field.required && !field.affectsOutcome) {
      issues.push(`Field "${field.label || field.id}" is required but affectsOutcome is not set — cannot activate`);
    }
    if (field.required && field.affectsOutcome && !field.notYetInstruction) {
      issues.push(`Field "${field.label || field.id}" is required but has no Not-Yet recovery instruction — cannot activate`);
    }
    if (field.notYetStepIds?.length > 0) {
      field.notYetStepIds.forEach((sid) => {
        if (!stepIds.has(sid)) {
          issues.push(`Field "${field.label || field.id}" references Not-Yet step "${sid}" which does not exist`);
        }
      });
    }
    if (field.type === 'slider') {
      const fLabel = field.label || field.id;
      if (field.min == null) issues.push(`Slider "${fLabel}" is missing a min value`);
      if (field.max == null) issues.push(`Slider "${fLabel}" is missing a max value`);
      if (field.step == null) issues.push(`Slider "${fLabel}" is missing a step value`);
      if (field.min != null && field.max != null && field.max <= field.min) {
        issues.push(`Slider "${fLabel}" max must be greater than min`);
      }
      if (field.step != null && field.step <= 0) {
        issues.push(`Slider "${fLabel}" step must be greater than 0`);
      }
      if (!field.anchors || Object.keys(field.anchors).length === 0) {
        issues.push(`Slider "${fLabel}" needs at least one anchor label`);
      }
    }
  });

  steps.forEach((step) => {
    if (step.requiredArtifacts?.length > 0) {
      step.requiredArtifacts.forEach((art) => {
        if (art.type === 'field' && !fieldIds.has(art.refId)) {
          issues.push(`Not-Yet step "${step.label || step.id}" references field "${art.refId}" which does not exist`);
        }
      });
    }
  });

  const draftAttachments = (draft.attachmentRequirements || []).map(normalizeAttachment);
  draftAttachments.forEach((att) => {
    if (!att.label) {
      issues.push(`Attachment "${att.id}" is missing a label — cannot activate`);
    }
    if (!att.attachmentType) {
      issues.push(`Attachment "${att.label || att.id}" is missing an attachment type — cannot activate`);
    }
  });

  const intraFlags = draft.intraSpecialtyRedirectFlags || [];
  intraFlags.forEach((flag, idx) => {
    if (flag.target?.label && !flag.target?.targetId) {
      issues.push(`Intra-redirect #${idx + 1} ("${flag.label || 'untitled'}") has no linked target pack — cannot activate`);
    } else if (flag.target?.targetId && !otherPackIds.has(flag.target.targetId)) {
      issues.push(`Intra-redirect #${idx + 1} ("${flag.label || 'untitled'}") links to a pack that no longer exists`);
    }
  });

  return (
    <div>
      <SectionHeader title="Review & Publish" description="Summary of your pack before publishing." helpKey="review" activeFocusTarget={activeFocusTarget} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        <StatCard label="Clinical Fields" value={fieldCount} />
        <StatCard label="Red Flags" value={redFlagCount} />
        <StatCard label="Redirect Flags" value={redirectCount} />
        <StatCard label="Not-Yet Steps" value={stepCount} />
        <StatCard label="Attachment Requirements" value={attachmentCount} />
      </div>

      {issues.length > 0 && (
        <div style={{ background: 'var(--rg-status-not-yet-bg)', border: '1px solid var(--rg-status-not-yet-border)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <h4 style={{ fontSize: 13, fontWeight: 600, color: 'var(--rg-status-not-yet-text)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <AlertTriangle size={14} /> Issues to Review
          </h4>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {issues.map((issue, i) => <li key={i} style={{ fontSize: 13, color: 'var(--rg-status-not-yet-text)', marginBottom: 4 }}>{issue}</li>)}
          </ul>
        </div>
      )}

      {issues.length === 0 && (
        <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(52,211,153,0.4)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <p style={{ fontSize: 13, color: '#6ee7b7', display: 'flex', alignItems: 'center', gap: 6 }}>
            <CheckCircle size={14} /> Pack looks ready to publish!
          </p>
        </div>
      )}

      <PackPreview draft={draft} />
    </div>
  );
}

function PreviewSelectChevron() {
  return (
    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
      <svg className="h-5 w-5 text-slate-400" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
      </svg>
    </div>
  );
}

function PreviewSeveritySlider({ field }) {
  const min = field.min ?? 0;
  const max = field.max ?? 10;
  const mid = Math.round((max - min) * 0.3 + min);
  const pct = ((mid - min) / (max - min)) * 100;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-400">Mild</span>
        <span className="text-lg font-bold text-emerald-400">{mid}/{max}</span>
        <span className="text-sm text-slate-400">Severe</span>
      </div>
      <div className="relative">
        <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-emerald-600 to-emerald-400"
            style={{ width: `${pct}%` }}
          />
        </div>
        <input
          type="range" min={min} max={max} step={1} value={mid}
          disabled
          className="absolute inset-0 w-full h-full opacity-0 cursor-not-allowed"
        />
      </div>
    </div>
  );
}

function PreviewField({ field }) {
  if (field.type === 'textarea' || field.type === 'text') return null;

  const label = (
    <label className="block text-sm font-medium text-slate-300">
      {field.label || 'Untitled field'}
      {field.required && <span className="text-red-400 ml-1">*</span>}
    </label>
  );
  const help = field.helpText && <p className="text-xs text-slate-500">{field.helpText}</p>;

  if (field.type === 'severity' || field.type === 'slider') {
    return (
      <div key={field.id} className="space-y-2">
        {label}
        <PreviewSeveritySlider field={field} />
        {help}
      </div>
    );
  }

  if (field.type === 'number') {
    const isSlider = field.min !== undefined && field.max !== undefined &&
      ((field.min === 0 && field.max === 10) || (field.min === 1 && field.max === 10));
    if (isSlider) {
      return (
        <div key={field.id} className="space-y-2">
          {label}
          <PreviewSeveritySlider field={field} />
          {help}
        </div>
      );
    }
    return (
      <div key={field.id} className="space-y-2">
        {label}
        <input
          type="number"
          disabled
          placeholder={field.placeholder || 'Enter number…'}
          className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-slate-500 cursor-not-allowed"
        />
        {help}
      </div>
    );
  }

  if (field.type === 'select') {
    const opts = (field.options || []).filter(o => o.label || o.value);
    return (
      <div key={field.id} className="space-y-2">
        {label}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 text-sm text-slate-500 border-b border-slate-700/30">Select one option…</div>
          {opts.length > 0 ? opts.map((opt, i) => (
            <div
              key={opt.value || i}
              className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-700/20 last:border-b-0 cursor-not-allowed"
            >
              <div className="w-4 h-4 rounded-full border-2 border-slate-600 flex-shrink-0" />
              <span className="text-sm text-slate-300">{opt.label || opt.value}</span>
            </div>
          )) : (
            <div className="px-4 py-2.5 text-sm text-slate-500 italic">No options defined yet</div>
          )}
        </div>
        {help}
      </div>
    );
  }

  if (field.type === 'multiselect') {
    const opts = (field.options || []).filter(o => o.label || o.value);
    return (
      <div key={field.id} className="space-y-2">
        {label}
        {opts.length > 0 ? (
          <div className="grid grid-cols-2 gap-2">
            {opts.map((opt, i) => (
              <div
                key={opt.value || i}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left bg-slate-800/50 border border-slate-700/50 text-slate-300 cursor-not-allowed"
              >
                <Square className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                {opt.label || opt.value}
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-2.5 text-sm text-slate-500 italic">No options defined yet</div>
        )}
        {help}
      </div>
    );
  }

  if (field.type === 'boolean') {
    return (
      <div key={field.id} className="space-y-2">
        {label}
        <div className="flex gap-2">
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-800/50 border border-slate-700/50 cursor-not-allowed">
            <div className="w-4 h-4 rounded-full border-2 border-slate-600 flex-shrink-0" />
            <span className="text-sm text-slate-300">Yes</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-800/50 border border-slate-700/50 cursor-not-allowed">
            <div className="w-4 h-4 rounded-full border-2 border-slate-600 flex-shrink-0" />
            <span className="text-sm text-slate-300">No</span>
          </div>
        </div>
        {help}
      </div>
    );
  }

  if (field.type === 'date') {
    return (
      <div key={field.id} className="space-y-2">
        {label}
        <input
          type="text"
          disabled
          placeholder="Select date…"
          className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-slate-500 cursor-not-allowed"
        />
        {help}
      </div>
    );
  }

  return null;
}

function PackPreview({ draft }) {
  const [previewOpen, setPreviewOpen] = useState(true);
  const fields = draft.fields || [];
  const redFlags = draft.redFlags || [];
  const intraRedirects = draft.intraSpecialtyRedirectFlags || [];
  const crossRedirects = draft.crossSpecialtyRedirectFlags || [];
  const hasIntraRedirects = intraRedirects.length > 0;
  const hasCrossRedirects = crossRedirects.length > 0;
  const hasAnyRedirects = hasIntraRedirects || hasCrossRedirects;
  const attachments = (draft.attachmentRequirements || []).map(normalizeAttachment);
  const requiredFields = fields.filter(f => f.required);
  const additionalFields = fields.filter(f => !f.required);
  const steps = draft.notYetNextSteps || [];
  const patientMessages = draft.patientMessages || {};
  const escalationOpts = (draft.escalationOptions || []).filter(o => o !== 'Other' && o !== 'Other reason');
  const hasContent = fields.length > 0 || redFlags.length > 0 || hasAnyRedirects || attachments.length > 0;

  return (
    <div className="bg-slate-900/50 border border-slate-700/50 rounded-xl p-6 mb-4">
      {/* Header — clickable to collapse/expand */}
      <button
        type="button"
        onClick={() => setPreviewOpen((v) => !v)}
        className="w-full flex items-center justify-between mb-0"
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
      >
        <div className="flex items-center gap-2">
          <Eye className="w-5 h-5 text-slate-400" />
          <h4 className="text-base font-semibold text-white">Referring Provider View</h4>
          {previewOpen
            ? <ChevronUp className="w-4 h-4 text-slate-500" />
            : <ChevronDown className="w-4 h-4 text-slate-500" />}
        </div>
        <span className="inline-flex items-center gap-1.5 bg-indigo-500/15 border border-indigo-500/30 rounded px-2 py-1 text-xs text-indigo-300 font-medium">
          <Lock className="w-3 h-3" /> Preview only — read-only
        </span>
      </button>

      {previewOpen && (<>
      <div style={{ marginTop: 24 }} />

      {draft.name && draft.name.trim() && (
        <p className="text-sm text-slate-400 mb-6">{draft.name}</p>
      )}

      {!hasContent && (
        <p className="text-sm text-slate-500 text-center py-8">No pack content to preview. Add fields, flags, or attachments first.</p>
      )}

      {hasContent && (
        <div className="space-y-8">

          {/* Intra-Specialty Redirect Flags */}
          {hasIntraRedirects && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                </div>
                <h3 className="text-base font-semibold text-white">Same Specialty — Routing Check</h3>
                <span className="text-xs text-slate-500">Check if the primary issue fits a different pathway within this specialty</span>
              </div>
              <div className="space-y-3">
                {intraRedirects.map((flag, i) => (
                  <div
                    key={flag.id || i}
                    className="w-full p-4 rounded-xl border text-left bg-slate-800/40 border-slate-700/50 cursor-not-allowed"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0 mt-0.5 bg-slate-700">
                        <Square className="w-4 h-4 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{flag.label || 'Untitled redirect'}</p>
                        {flag.description && <p className="text-sm text-slate-500 mt-0.5">{flag.description}</p>}
                        {flag.guidance && <p className="text-sm text-slate-500 mt-0.5">{flag.guidance}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Cross-Specialty Redirect Flags */}
          {hasCrossRedirects && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-violet-400" />
                </div>
                <h3 className="text-base font-semibold text-white">Different Specialty — Routing Check</h3>
                <span className="text-xs text-slate-500">Check if this referral belongs in a different specialty</span>
              </div>
              <div className="space-y-3">
                {crossRedirects.map((flag, i) => (
                  <div
                    key={flag.id || i}
                    className="w-full p-4 rounded-xl border text-left bg-slate-800/40 border-slate-700/50 cursor-not-allowed"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0 mt-0.5 bg-slate-700">
                        <Square className="w-4 h-4 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{flag.label || 'Untitled redirect'}</p>
                        {flag.description && <p className="text-sm text-slate-500 mt-0.5">{flag.description}</p>}
                        {flag.guidance && <p className="text-sm text-slate-500 mt-0.5">{flag.guidance}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Visit Information */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Clipboard className="w-4 h-4 text-blue-400" />
              </div>
              <h3 className="text-base font-semibold text-white">Visit Information</h3>
            </div>
            <div className="space-y-4 bg-slate-800/30 border border-slate-700/50 rounded-xl p-4">
              {/* Visit Type */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-300">
                  Visit Type <span className="text-red-400">*</span>
                </label>
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg overflow-hidden">
                  <div className="px-4 py-2.5 text-sm text-slate-500 border-b border-slate-700/30">Select one option…</div>
                  {[
                    { value: 'in-person', label: 'In-Person' },
                    { value: 'video', label: 'Video (Telehealth)' },
                    { value: 'phone', label: 'Phone (Telehealth)' },
                    { value: 'async', label: 'Async Message' },
                  ].map(o => (
                    <div key={o.value} className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-700/20 last:border-b-0 cursor-not-allowed">
                      <div className="w-4 h-4 rounded-full border-2 border-slate-600 flex-shrink-0" />
                      <span className="text-sm text-slate-300">{o.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Reason for Escalation */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-300">
                  Reason for Escalation <span className="text-red-400">*</span>
                </label>
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg overflow-hidden">
                  <div className="px-4 py-2.5 text-sm text-slate-500 border-b border-slate-700/30">Select one option…</div>
                  {escalationOpts.length > 0 ? escalationOpts.map(reason => (
                    <div key={reason} className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-700/20 last:border-b-0 cursor-not-allowed">
                      <div className="w-4 h-4 rounded-full border-2 border-slate-600 flex-shrink-0" />
                      <span className="text-sm text-slate-300">{reason}</span>
                    </div>
                  )) : (
                    <div className="px-4 py-2.5 text-sm text-slate-500 italic">No escalation reasons defined in pack</div>
                  )}
                </div>
              </div>
              {/* Visit Purpose */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-300">
                  Visit Purpose
                </label>
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg overflow-hidden">
                  <div className="px-4 py-2.5 text-sm text-slate-500 border-b border-slate-700/30">Select one option…</div>
                  {[
                    { value: 'NEW_PATIENT_INITIAL_CONSULT', label: 'New Patient / Initial Consult' },
                    { value: 'FOLLOW_UP', label: 'Follow-up' },
                    { value: 'PROCEDURE_TESTING', label: 'Procedure / Testing' },
                    { value: 'SECOND_OPINION', label: 'Second Opinion' },
                    { value: 'URGENT_CONSULT', label: 'Urgent Consult' },
                  ].map(o => (
                    <div key={o.value} className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-700/20 last:border-b-0 cursor-not-allowed">
                      <div className="w-4 h-4 rounded-full border-2 border-slate-600 flex-shrink-0" />
                      <span className="text-sm text-slate-300">{o.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Red Flags */}
          {redFlags.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                </div>
                <h3 className="text-base font-semibold text-white">Red Flags</h3>
                <span className="text-xs text-slate-500">Check any concerning symptoms present</span>
              </div>
              <div className="space-y-3">
                {redFlags.map((flag, i) => (
                  <div
                    key={flag.id || i}
                    className="w-full p-4 rounded-xl border text-left bg-slate-800/40 border-slate-700/50 cursor-not-allowed"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0 mt-0.5 bg-slate-700">
                        <Square className="w-4 h-4 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{flag.label || 'Untitled red flag'}</p>
                        {flag.description && <p className="text-sm text-slate-500 mt-0.5">{flag.description}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Required Minimum Workup */}
          {requiredFields.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                </div>
                <h3 className="text-base font-semibold text-white">Required Minimum Workup</h3>
                <span className="text-xs text-red-400 font-medium">All required</span>
              </div>
              <div className="space-y-4 bg-slate-800/30 border border-slate-700/50 rounded-xl p-4">
                {requiredFields.map(field => (
                  <PreviewField key={field.id} field={field} />
                ))}
              </div>
            </section>
          )}

          {/* Additional Clinical Data */}
          {additionalFields.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center">
                  <Clipboard className="w-4 h-4 text-violet-400" />
                </div>
                <h3 className="text-base font-semibold text-white">Additional Clinical Data</h3>
                <span className="text-xs text-slate-500">Optional</span>
              </div>
              <div className="space-y-4 bg-slate-800/30 border border-slate-700/50 rounded-xl p-4">
                {additionalFields.map(field => (
                  <PreviewField key={field.id} field={field} />
                ))}
              </div>
            </section>
          )}

          {/* Attachments */}
          {attachments.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-slate-700/50 flex items-center justify-center">
                  <Paperclip className="w-4 h-4 text-slate-400" />
                </div>
                <h3 className="text-base font-semibold text-white">Attachments</h3>
                <span className="text-xs text-slate-500">Labs, imaging, and supporting documents</span>
              </div>
              <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4 space-y-3">
                {attachments.map((att, i) => (
                  <div key={att.id || i} className="flex items-center gap-3 p-3 bg-slate-800/50 border border-slate-700/50 rounded-lg">
                    <Paperclip className="w-4 h-4 text-slate-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">{att.label || 'Untitled attachment'}</p>
                      {att.attachmentType && (
                        <span className="text-xs text-slate-500">{ATTACHMENT_TYPE_LABELS[att.attachmentType] || att.attachmentType}</span>
                      )}
                    </div>
                    {att.requiredForIntakeCompleteness && (
                      <span className="text-[10px] font-semibold text-amber-400 bg-amber-500/15 border border-amber-500/30 rounded px-1.5 py-0.5 whitespace-nowrap">
                        REQUIRED FOR INTAKE
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Not-Yet Recovery Steps */}
          {steps.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-emerald-400" />
                </div>
                <h3 className="text-base font-semibold text-white">Not-Yet Recovery Steps</h3>
                <span className="text-xs text-slate-500">Shown to referring provider when outcome is NOT_YET</span>
              </div>
              <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4 space-y-2">
                {steps.map((step, i) => (
                  <div key={step.id || i} className="flex items-center gap-3 p-3 bg-slate-800/50 border border-slate-700/50 rounded-lg">
                    <Shield className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <p className="text-sm text-white">{step.label || 'Untitled step'}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Patient Messages */}
          {(patientMessages.routine || patientMessages.notYet || patientMessages.urgent) && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <MessageSquare className="w-4 h-4 text-blue-400" />
                </div>
                <h3 className="text-base font-semibold text-white">Patient Messages</h3>
              </div>
              <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4 space-y-3">
                {patientMessages.routine && (
                  <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                    <p className="text-[11px] font-semibold text-emerald-400 uppercase mb-1">Routine</p>
                    <p className="text-sm text-slate-300">{patientMessages.routine}</p>
                  </div>
                )}
                {patientMessages.notYet && (
                  <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-[11px] font-semibold text-amber-400 uppercase mb-1">Not Yet</p>
                    <p className="text-sm text-slate-300">{patientMessages.notYet}</p>
                  </div>
                )}
                {patientMessages.urgent && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-[11px] font-semibold text-red-400 uppercase mb-1">Urgent</p>
                    <p className="text-sm text-slate-300">{patientMessages.urgent}</p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Disabled Submit Button */}
          <div className="flex items-center justify-between pt-4 border-t border-slate-700/30">
            <p className="text-xs text-slate-500">Preview only — this form cannot be submitted</p>
            <button
              disabled
              className="flex items-center gap-2 px-8 py-3 rounded-xl text-white font-semibold bg-slate-700 cursor-not-allowed"
            >
              <Lock className="w-5 h-5" />
              Submit Referral Decision
            </button>
          </div>

        </div>
      )}
      </>)}
    </div>
  );
}

// ============================================================================
// SHARED COMPONENTS
// ============================================================================

function PackListItem({ pack, onOpen, onArchive, onDelete, onHistory }) {
  const isDraft = pack.status === PACK_STATUS.DRAFT;
  const isActive = pack.status === PACK_STATUS.ACTIVE;
  const packData = pack.pack || {};
  const fieldCount = (pack.fields?.length || packData.fields?.length || 0);
  const displayName = pack.name || packData.metadata?.name || 'Untitled';
  const displayVersion = pack.versionId || packData.metadata?.version || '1.0.0';

  return (
    <div style={{ background: 'var(--rg-bg-surface)', border: '1px solid var(--rg-border-subtle)', borderRadius: 8, padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div>
        <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4, color: 'var(--rg-text-primary)' }}>{displayName}</h3>
        <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--rg-text-secondary)' }}>
          <span style={{ color: statusColor(pack.status), fontWeight: 600, textTransform: 'uppercase' }}>{pack.status}</span>
          <span>v{displayVersion}</span>
          <span>{fieldCount} fields</span>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={() => onHistory({ specialtyId: pack.specialtyId, packId: pack.packId, name: displayName })} style={btnGhost} title="Version history">
          <Clock size={13} /> History
        </button>
        {isDraft && (
          <>
            <button onClick={() => onOpen(pack)} style={btnSecondary}><Edit3 size={13} /> Edit</button>
            <button onClick={() => onDelete(pack)} style={btnDanger}><Trash2 size={13} /></button>
          </>
        )}
        {isActive && (
          <>
            <button onClick={() => onOpen(pack)} style={btnSecondary}><Edit3 size={13} /> Edit</button>
            <button onClick={() => onArchive(pack)} style={btnGhost}><Archive size={13} /> Archive</button>
          </>
        )}
        {pack.status === PACK_STATUS.ARCHIVED && (
          <button onClick={() => onOpen(pack)} style={btnGhost}><Eye size={13} /> View</button>
        )}
      </div>
    </div>
  );
}

function SectionHeader({ title, description, helpKey, activeFocusTarget }) {
  const [showHelp, setShowHelp] = useState(false);
  const help = helpKey ? PACK_BUILDER_HELP[helpKey] : null;
  const isHighlighted = activeFocusTarget === 'pack-editor-guidance-icon';

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4, color: 'var(--rg-text-primary)', flex: 1 }}>{title}</h2>
        {help && (
          <button
            onClick={() => setShowHelp(true)}
            className={isHighlighted ? 'ring-2 ring-emerald-400/20 rounded-lg animate-pulse' : ''}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 6,
              color: isHighlighted ? '#6ee7b7' : 'var(--rg-text-muted)', display: 'flex', alignItems: 'center',
              transition: 'color 0.3s, box-shadow 0.3s',
            }}
            title="Open section guidance"
            aria-label={`Open section guidance for ${title}`}
            data-learning-target="pack-editor-guidance-icon"
          >
            <HelpCircle size={20} />
          </button>
        )}
      </div>
      {description && <p style={{ fontSize: 13, color: 'var(--rg-text-secondary)' }}>{description}</p>}
      {help && showHelp && <HelpDrawer help={help} onClose={() => setShowHelp(false)} />}
    </div>
  );
}

function HelpDrawer({ help, onClose }) {
  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'var(--rg-help-scrim)', zIndex: 999,
        }}
      />
      <div
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: 380, maxWidth: '90vw',
          background: 'var(--rg-help-bg)', borderLeft: '1px solid var(--rg-help-border)',
          zIndex: 1000, overflowY: 'auto', padding: '24px 20px',
          display: 'flex', flexDirection: 'column', gap: 16,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--rg-help-text-strong)', margin: 0 }}>{help.title}</h3>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--rg-help-label)', padding: 4 }}
            aria-label="Close help"
          >
            <X size={18} />
          </button>
        </div>

        <HelpBlock label="What this section is for" text={help.purpose} />

        <div style={{
          background: 'var(--rg-help-accent-bg)', border: '1px solid var(--rg-help-accent-border)',
          borderRadius: 8, padding: 12,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--rg-help-accent-eyebrow)', textTransform: 'uppercase', marginBottom: 4 }}>
            Question to ask yourself
          </div>
          <p style={{ fontSize: 13, color: 'var(--rg-help-accent-text)', margin: 0, fontStyle: 'italic' }}>{help.question}</p>
        </div>

        {help.important && (
          <div style={{
            background: 'var(--rg-help-warn-bg)', border: '1px solid var(--rg-help-warn-border)',
            borderRadius: 8, padding: 12,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--rg-help-warn-eyebrow)', textTransform: 'uppercase', marginBottom: 4 }}>
              Important
            </div>
            <p style={{ fontSize: 13, color: 'var(--rg-help-accent-text)', margin: 0 }}>{help.important}</p>
          </div>
        )}

        <HelpBlock label="Good input" text={help.goodInput} />
        <HelpBlock label="Example" text={help.example} mono />

        {help.notYetGuidance && (
          <div style={{
            background: 'var(--rg-help-accent-bg-soft)', border: '1px solid var(--rg-help-accent-border-soft)',
            borderRadius: 8, padding: 12,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--rg-help-accent-eyebrow)', textTransform: 'uppercase', marginBottom: 4 }}>
              Required for Routine + Not-Yet
            </div>
            <p style={{ fontSize: 13, color: 'var(--rg-help-text)', margin: 0, whiteSpace: 'pre-line' }}>{help.notYetGuidance}</p>
          </div>
        )}

        <HelpBlock label="Avoid" text={help.avoid} warn />

        {help.subSections && help.subSections.map((sub, idx) => (
          <React.Fragment key={idx}>
            <div style={{
              borderTop: '1px solid var(--rg-help-divider)', paddingTop: 16, marginTop: 4,
            }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, color: 'var(--rg-help-text-strong)', margin: '0 0 8px' }}>{sub.heading}</h4>
              <HelpBlock label="What this field should do" text={sub.purpose} />
            </div>
            {sub.question && (
              <div style={{
                background: 'var(--rg-help-accent-bg)', border: '1px solid var(--rg-help-accent-border)',
                borderRadius: 8, padding: 12,
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--rg-help-accent-eyebrow)', textTransform: 'uppercase', marginBottom: 4 }}>
                  Question to ask yourself
                </div>
                <p style={{ fontSize: 13, color: 'var(--rg-help-accent-text)', margin: 0, fontStyle: 'italic' }}>{sub.question}</p>
              </div>
            )}
            <HelpBlock label="Good input" text={sub.goodInput} />
            <HelpBlock label="Example" text={sub.example} mono />
            <HelpBlock label="Avoid" text={sub.avoid} warn />
          </React.Fragment>
        ))}
      </div>
    </>
  );
}

function HelpBlock({ label, text, mono, warn }) {
  if (!text) return null;
  return (
    <div>
      <div style={{
        fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4,
        color: warn ? 'var(--rg-help-avoid)' : 'var(--rg-help-label)',
      }}>
        {label}
      </div>
      <p style={{
        fontSize: 13, color: 'var(--rg-help-text)', margin: 0, whiteSpace: 'pre-line',
        ...(mono ? { fontFamily: 'monospace', background: 'var(--rg-help-code-bg)', border: '1px solid var(--rg-help-code-border)', padding: 8, borderRadius: 6, fontSize: 12 } : {}),
      }}>
        {text}
      </p>
    </div>
  );
}

function FormGroup({ label, children, compact }) {
  return (
    <div style={{ marginBottom: compact ? 10 : 16 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--rg-text-secondary)', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

// ============================================================================
// EVIDENCE ASSIST AUTHORING UI (Phase 2C-1)
//
// Compact, collapsed-by-default controls for authoring Pack-local evidence
// maps. Never-PHI: only concepts, aliases, keywords, phrases, units, and
// search rules. These do NOT match documents, render chips, or change Gate
// outcomes in this phase — they only define the evidence search vocabulary
// consumed by later phases.
// ============================================================================

/** Split a comma/newline-separated input into a trimmed, non-empty string[]. */
function parseListInput(text) {
  return (text || '').split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
}

function listsEqual(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/**
 * List editor backed by a string[] value. Local text state lets the author
 * type commas/spaces freely; the parent only ever receives a parsed array.
 * Re-syncs from the parent only on genuine external changes (pack switch /
 * reset), never on the author's own keystrokes.
 */
function ListInput({ value, onChange, placeholder, multiline }) {
  const [text, setText] = useState(() => (Array.isArray(value) ? value : []).join(', '));
  useEffect(() => {
    const incoming = Array.isArray(value) ? value : [];
    if (!listsEqual(incoming, parseListInput(text))) {
      setText(incoming.join(', '));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(Array.isArray(value) ? value : [])]);

  const handle = (t) => {
    setText(t);
    onChange(parseListInput(t));
  };

  const style = {
    ...inputStyle,
    fontSize: 12,
    padding: '6px 8px',
    ...(multiline ? { minHeight: 44, resize: 'vertical' } : {}),
  };
  return multiline
    ? <textarea value={text} onChange={(e) => handle(e.target.value)} style={style} placeholder={placeholder} />
    : <input type="text" value={text} onChange={(e) => handle(e.target.value)} style={style} placeholder={placeholder} />;
}

function EvidenceRow({ label, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--rg-text-muted)', marginBottom: 3 }}>{label}</label>
      {children}
    </div>
  );
}

/**
 * Collapsible "Evidence Assist" container. Collapsed by default to stay compact.
 *
 * Drafting assistant (Phase 2C-1.5): when `onSuggest` + `onApplySuggestion` are
 * provided, renders a "Suggest evidence map" action that drafts values from
 * Pack text only. The specialist still reviews/edits/accepts — drafting never
 * saves or publishes on its own. If the target already has values, a Replace /
 * Cancel confirmation is shown first.
 */
// Subtle status chip colors for the background Evidence Assist state.
const EVIDENCE_STATUS_TONE = {
  [EVIDENCE_STATUS.READY]:          { color: '#6ee7b7', opacity: 1 },
  [EVIDENCE_STATUS.NEEDS_REVIEW]:   { color: '#facc15', opacity: 1 },
  [EVIDENCE_STATUS.NOT_CONFIGURED]: { color: 'var(--rg-text-muted)', opacity: 0.6 },
};

/**
 * Advanced evidence settings — Phase 2C-2d.
 *
 * Evidence maps are now prepared in the BACKGROUND on Save/Publish (see
 * autoLinkPackEvidence). This is no longer part of the normal specialist flow:
 * the normal view shows only a tiny "Advanced evidence settings" line + a subtle
 * status chip. The concept/alias/phrase form is collapsed by default and appears
 * only when the specialist deliberately opens this advanced/debug section.
 */
function EvidenceAssistPanel({ children, title = 'Advanced evidence settings', subtitle, onSuggest, onApplySuggestion, hasExistingEvidence, evidenceMap }) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [drafted, setDrafted] = useState(false);
  const canSuggest = typeof onSuggest === 'function' && typeof onApplySuggestion === 'function';
  const status = evidenceMapStatus(evidenceMap);
  const tone = EVIDENCE_STATUS_TONE[status] || EVIDENCE_STATUS_TONE[EVIDENCE_STATUS.NOT_CONFIGURED];

  const applyDraft = () => {
    onApplySuggestion(onSuggest());
    setConfirming(false);
    setDrafted(true);
  };
  const handleSuggestClick = () => {
    if (hasExistingEvidence) { setConfirming(true); return; }
    applyDraft();
  };

  return (
    <div style={{ marginTop: 8, borderTop: '1px solid var(--rg-border-subtle)', paddingTop: 6 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Optional. Evidence maps are prepared automatically in the background — you do not need to configure them."
        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--rg-text-muted)', padding: 0, fontSize: 11, fontWeight: 500 }}
      >
        <Search size={11} /> {title}
        <span style={{ fontSize: 10, fontWeight: 600, color: tone.color, opacity: tone.opacity }}>
          · {EVIDENCE_STATUS_LABELS[status]}
        </span>
        {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
      </button>
      {open && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {subtitle && <p style={{ fontSize: 11, color: 'var(--rg-text-muted)', margin: 0, lineHeight: 1.4 }}>{subtitle}</p>}
          {canSuggest && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {confirming ? (
                <div style={{ background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.3)', borderRadius: 6, padding: 8 }}>
                  <p style={{ fontSize: 11, color: '#facc15', margin: '0 0 6px' }}>
                    Replace existing Evidence Assist values with a new draft?
                  </p>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button type="button" onClick={applyDraft} style={suggestBtnStyle}>
                      <Sparkles size={12} /> Replace draft
                    </button>
                    <button type="button" onClick={() => setConfirming(false)} style={suggestCancelStyle}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button type="button" onClick={handleSuggestClick} style={suggestBtnStyle}>
                  <Sparkles size={12} /> Suggest evidence map
                </button>
              )}
              {drafted && !confirming && (
                <p style={{ fontSize: 11, color: '#6ee7b7', margin: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Sparkles size={11} /> Drafted from this Pack text. Review before publishing.
                </p>
              )}
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  );
}

/** Field-level Evidence Assist authoring block. */
function FieldEvidenceAssist({ field, onChange, onApplyDraft }) {
  const em = field.evidenceMap || {};
  return (
    <EvidenceAssistPanel
      evidenceMap={field.evidenceMap}
      subtitle="Advanced/optional. Prepared automatically in the background. Pack-defined keywords used later to surface document evidence beside this field. Never auto-fills — the referring provider always selects or types the value. No patient data here."
      onSuggest={() => draftFieldEvidenceMap(field)}
      onApplySuggestion={onApplyDraft}
      hasExistingEvidence={evidenceMapHasValues(em)}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <EvidenceRow label="Concept ID">
          <input type="text" value={em.conceptId || ''} onChange={(e) => onChange('conceptId', e.target.value)} style={evidenceInputStyle} placeholder="e.g. white_blood_cell_count" />
        </EvidenceRow>
        <EvidenceRow label="Canonical label">
          <input type="text" value={em.canonicalLabel || ''} onChange={(e) => onChange('canonicalLabel', e.target.value)} style={evidenceInputStyle} placeholder="e.g. White blood cell count" />
        </EvidenceRow>
      </div>
      <EvidenceRow label="Aliases (comma or new line)">
        <ListInput value={em.aliases} onChange={(v) => onChange('aliases', v)} placeholder="WBC, WCC, white blood cell count" />
      </EvidenceRow>
      <EvidenceRow label="Phrases (one per line)">
        <ListInput multiline value={em.phrases} onChange={(v) => onChange('phrases', v)} placeholder="e.g. white cell count of" />
      </EvidenceRow>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <EvidenceRow label="Value type">
          <select value={em.valueType || ''} onChange={(e) => onChange('valueType', e.target.value)} style={evidenceInputStyle}>
            <option value="">—</option>
            {EVIDENCE_VALUE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </EvidenceRow>
        <EvidenceRow label="Evidence mode">
          <select value={em.evidenceMode || ''} onChange={(e) => onChange('evidenceMode', e.target.value)} style={evidenceInputStyle}>
            <option value="">—</option>
            {EVIDENCE_FIELD_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </EvidenceRow>
      </div>
      <EvidenceRow label="Units (comma separated)">
        <ListInput value={em.units} onChange={(v) => onChange('units', v)} placeholder="x10^9/L, K/uL" />
      </EvidenceRow>
      <EvidenceRow label="Negative phrases (one per line)">
        <ListInput multiline value={em.negativePhrases} onChange={(v) => onChange('negativePhrases', v)} placeholder="suppress a false match" />
      </EvidenceRow>
      <EvidenceRow label="Uncertainty phrases (one per line)">
        <ListInput multiline value={em.uncertaintyPhrases} onChange={(v) => onChange('uncertaintyPhrases', v)} placeholder="mark evidence as uncertain" />
      </EvidenceRow>
    </EvidenceAssistPanel>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={{ background: 'var(--rg-bg-surface)', border: '1px solid var(--rg-border-subtle)', borderRadius: 8, padding: 16, textAlign: 'center' }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--rg-text-primary)' }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--rg-text-secondary)' }}>{label}</div>
    </div>
  );
}

function ErrorBanner({ message, onDismiss }) {
  return (
    <div style={{ background: 'rgba(127,29,29,0.3)', border: '1px solid rgba(248,113,113,0.4)', borderRadius: 8, padding: 12, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontSize: 13, color: '#fca5a5' }}>{message}</span>
      {onDismiss && <button onClick={onDismiss} style={btnIcon}><X size={14} /></button>}
    </div>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const statusColor = (status) => {
  switch (status) {
    case 'draft': return '#fbbf24';
    case 'active': return '#34d399';
    case 'archived': return '#9ca3af';
    default: return '#9ca3af';
  }
};

const inputStyle = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid var(--rg-border-subtle)',
  borderRadius: 6,
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
  background: 'var(--rg-bg-muted)',
  color: 'var(--rg-text-primary)',
};

// Compact input style for Evidence Assist authoring controls.
const evidenceInputStyle = {
  ...inputStyle,
  fontSize: 12,
  padding: '6px 8px',
};

// "Suggest evidence map" drafting-assistant button (Phase 2C-1.5).
const suggestBtnStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '5px 10px', background: 'rgba(99,102,241,0.15)', color: '#a5b4fc',
  border: '1px solid rgba(99,102,241,0.35)', borderRadius: 6,
  fontSize: 12, fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-start',
};

const suggestCancelStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '5px 10px', background: 'transparent', color: 'var(--rg-text-secondary)',
  border: '1px solid var(--rg-border-subtle)', borderRadius: 6,
  fontSize: 12, fontWeight: 500, cursor: 'pointer',
};

const btnPrimary = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 16px', background: '#6366f1', color: '#fff',
  border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

const btnSecondary = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '6px 12px', background: 'var(--rg-bg-muted)', color: 'var(--rg-text-secondary)',
  border: '1px solid var(--rg-border-subtle)', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer',
};

const btnGhost = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '6px 12px', background: 'transparent', color: '#818cf8',
  border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer',
};

const btnDanger = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '6px 12px', background: 'rgba(127,29,29,0.3)', color: '#f87171',
  border: '1px solid rgba(248,113,113,0.3)', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer',
};

const btnDisabled = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 16px', background: 'var(--rg-disabled-bg)', color: 'var(--rg-disabled-text)',
  border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'not-allowed',
};

const btnIcon = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 28, background: 'transparent', color: 'var(--rg-text-muted)',
  border: 'none', borderRadius: 4, cursor: 'pointer',
};

const cardStyle = {
  background: 'var(--rg-bg-surface)',
  border: '1px solid var(--rg-border-subtle)',
  borderRadius: 8,
  padding: 16,
  marginBottom: 12,
};
